/**
 * 业务页处理器与状态（从单体 index.html 迁出）。
 * 鉴权 / Runtime / 上传 / 导航 / 手册 / 审批横幅已拆到独立模块。
 */
import { requestRuntime, runtimeBaseUrl } from '../api/runtime.js';
import {
  applyRbacUiGates,
  canWriteBusiness,
  getRuntimeApiKey,
  getRuntimeWhoami,
  initAuthChrome,
  refreshRuntimeWhoami,
} from '../auth.js';
import { assertUploadFile, readBpFilePayload } from '../upload.js';
import { initNav, navigatePortalPage } from '../nav.js';
import { initEnterpriseHandbook, initHandbookToc } from '../docs-viewer.js';
import {
  buildApprovalBannerHtml,
  isAwaitingApproval,
  updateP2ApprovalBanner,
} from '../approval-banner.js';
import { escapeHtml } from '../util/html.js';
import { initValueCreationUi } from './value-creation-ui.js';
import { initPostInvestmentDashboardUi } from './post-investment-dashboard-ui.js';
import { initRedTeamUi } from './red-team-ui.js';
import { initPostRiskUi } from './post-risk-ui.js';
import { initKnowledgeUi } from './knowledge-ui.js';
import { initCopilotUi } from './copilot-ui.js';

const BP_EXTERNAL_FIELD_MAP = {
  '企业名称': 'companyName', '公司名称': 'companyName', '名称': 'companyName',
  '所属行业': 'industry', '行业': 'industry',
  '细分赛道': 'subSector', '赛道': 'subSector',
  '所在地区': 'region', '地区': 'region', '注册地址': 'region',
  '成立时间': 'foundedAt', '成立日期': 'foundedAt',
  '创始人': 'founders', '主要人员': 'founders', '高管摘要': 'founders',
  '核心团队': 'coreTeam', '团队': 'coreTeam',
  '融资轮次': 'financingRound', '轮次': 'financingRound',
  '融资金额': 'financingAmount', '融资金额(万元)': 'financingAmount',
  '投前估值': 'preMoneyValuation', '投前估值(万元)': 'preMoneyValuation',
  '投后估值': 'postMoneyValuation', '投后估值(万元)': 'postMoneyValuation',
  '募资用途': 'useOfProceeds', '资金用途': 'useOfProceeds',
  '主营业务': 'mainBusiness', '经营范围': 'mainBusiness', '业务摘要': 'mainBusiness',
  '商业模式': 'businessModel', '模式': 'businessModel',
  '核心产品': 'coreProducts', '产品': 'coreProducts',
  '竞争优势': 'competitiveAdvantage', '优势': 'competitiveAdvantage',
  '主要客户': 'keyCustomers', '客户': 'keyCustomers',
  '目标客户': 'targetCustomers',
  '股东结构': 'shareholderStructure', '股东摘要': 'shareholderStructure', '股东': 'shareholderStructure',
  '历史融资': 'fundingHistory',
  '注册资本': 'registeredCapital', '注册资本(万元)': 'registeredCapital',
  '法定代表人': 'legalRepresentative', '法人': 'legalRepresentative',
  '统一社会信用代码': 'unifiedSocialCreditCode', '信用代码': 'unifiedSocialCreditCode',
  '联系人': 'contactPerson', '负责人': 'contactPerson',
  '联系方式': 'contactInfo', '联系电话': 'contactInfo', '邮箱': 'contactInfo',
  '出让股权': 'equityOffered', '出让股权比例': 'equityOffered',
  '收入': 'revenue', '营业收入': 'revenue', '营收': 'revenue', '营收(万元)': 'revenue',
  '毛利率': 'grossMargin', '利润': 'profit', '现金流': 'cashFlow'
};

initHandbookToc();
initEnterpriseHandbook();
initAuthChrome();

let currentProjectId = '';
let currentProjectInput = {};
let latestXiniuStatus = null;
let latestIcMaterials = null;
let latestBpParse = null;
let latestPreJudgment = null;
let pendingBpPayload = null;
let latestContractReview = null;
let latestContractCompare = null;
let latestContractAdvanced = null;
let latestDdQuestions = null;
let contractDdRiskHints = [];
let ddqActiveDimension = '';
let ddChecklistStore = {};
let contractMode = 'review';
// 前置研判状态提升到模块顶部声明，避免后续绑定异常导致 TDZ 未初始化
let latestScorecard = null;
let isLoadingPrejudgmentCheck = false;
let ensureKnowledgeBrowseLoaded = () => {};
let ensureCopilotContextLoaded = () => {};
let loadPostRiskClosures = async () => {};

initNav({
  getCurrentProjectId: () => currentProjectId,
  onPageEnter(pageId) {
    if (pageId === 'home') void loadWorkbench();
    if (pageId === 'p11') ensureKnowledgeBrowseLoaded();
    if (pageId === 'p12') ensureCopilotContextLoaded();
    if (pageId === 'p15') ensureExternalIntelLoaded();
    if (pageId === 'p16') void refreshDataProviders();
    if (pageId === 'p1') {
      refreshXiniuStatus();
      void loadProjectList();
    }
    if (pageId === 'p15') refreshXiniuStatus();
    if (pageId === 'p1r' && pendingBpPayload) {
      const payload = pendingBpPayload;
      pendingBpPayload = null;
      void startBpParse(payload);
    }
    if (pageId === 'p1s') {
      void loadPreJudgmentCheck();
    }
  },
});


const CONTRACT_MODE_META = {
  review: {
    emptyTitle: '上传合同后开始审阅',
    emptyCopy: '系统将抽取交易信息与 15 大类核心条款，结合风险规则库与模板比对，输出审阅底稿。'
  },
  compare: {
    emptyTitle: '上传两个版本后开始对比',
    emptyCopy: '系统将识别新增、修改、删除类条款变更，并按高/中/低重要性排序，忽略纯格式差异。'
  },
  consistency: {
    emptyTitle: '上传 1–4 份合同做一致性校验',
    emptyCopy: '单份检查内部冲突；多份检查跨合同金额、估值、持股与权利义务是否一致。'
  },
  supplement: {
    emptyTitle: '上传主协议与补充协议',
    emptyCopy: '分析补充协议对主协议的新增、修改、取代或澄清，并提示对投资人权益的影响。'
  },
  history: {
    emptyTitle: '上传当前合同与历史版本',
    emptyCopy: '对比当前合同与最多 3 份历史版本，输出条款演变趋势与关键建议。'
  }
};

function hideAllContractReports() {
  document.getElementById('contract-report').classList.remove('active');
  document.getElementById('contract-compare-report').classList.remove('active');
  document.getElementById('contract-advanced-report').classList.remove('active');
  document.getElementById('contract-empty').style.display = 'flex';
}

function setContractMode(mode) {
  contractMode = mode;
  document.querySelectorAll('[data-contract-mode]').forEach((tab) => {
    tab.classList.toggle('active', tab.dataset.contractMode === mode);
  });
  document.getElementById('contract-upload-single').style.display =
    mode === 'review' ? 'block' : 'none';
  document.getElementById('contract-type-wrap').style.display =
    mode === 'review' ? 'block' : 'none';
  document.getElementById('contract-upload-compare').style.display =
    mode === 'compare' ? 'block' : 'none';
  document.getElementById('contract-upload-consistency').style.display =
    mode === 'consistency' ? 'block' : 'none';
  document.getElementById('contract-upload-supplement').style.display =
    mode === 'supplement' ? 'block' : 'none';
  document.getElementById('contract-upload-history').style.display =
    mode === 'history' ? 'block' : 'none';
  document.getElementById('contract-review-btn').style.display =
    mode === 'review' ? 'inline-flex' : 'none';
  document.getElementById('contract-compare-btn').style.display =
    mode === 'compare' ? 'inline-flex' : 'none';
  document.getElementById('contract-consistency-btn').style.display =
    mode === 'consistency' ? 'inline-flex' : 'none';
  document.getElementById('contract-supplement-btn').style.display =
    mode === 'supplement' ? 'inline-flex' : 'none';
  document.getElementById('contract-history-btn').style.display =
    mode === 'history' ? 'inline-flex' : 'none';
  hideAllContractReports();
  const meta = CONTRACT_MODE_META[mode] || CONTRACT_MODE_META.review;
  document.getElementById('contract-empty-title').textContent = meta.emptyTitle;
  document.getElementById('contract-empty-copy').textContent = meta.emptyCopy;
}

document.querySelectorAll('[data-contract-mode]').forEach((tab) => {
  tab.addEventListener('click', function() {
    setContractMode(this.dataset.contractMode);
  });
});

document.getElementById('contract-from-dd').addEventListener('click', function() {
  if (!latestDdQuestions) {
    showRuntimeError('请先在「AI尽调问题助手」生成尽调问题清单。');
    return;
  }
  const overview = latestDdQuestions.ddOverview || {};
  const hints = [
    ...(overview.highRiskAreas || []),
    ...(overview.priorityOrder || []).slice(0, 5)
  ].filter(Boolean);
  contractDdRiskHints = [...new Set(hints)].slice(0, 12);
  const box = document.getElementById('contract-dd-hints');
  box.style.display = 'block';
  box.textContent = contractDdRiskHints.length
    ? `已带入 ${contractDdRiskHints.length} 条尽调风险提示，将在审阅时重点核验。`
    : '尽调结果中暂无可用风险提示。';
  if (!document.getElementById('contract-project-name').value.trim()) {
    document.getElementById('contract-project-name').value =
      latestDdQuestions.basicInfo?.projectName ||
      latestDdQuestions.basicInfo?.companyName ||
      '';
  }
});

document.getElementById('contract-file-input').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择文件';
});

document.getElementById('contract-file-base').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-base-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择旧版';
});

document.getElementById('contract-file-target').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-target-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择新版';
});

function formatFileListName(files, emptyText) {
  if (!files || !files.length) return emptyText;
  return [...files].map((file) => `${file.name} · ${(file.size / 1024).toFixed(1)} KB`).join('；');
}

document.getElementById('contract-file-consistency').addEventListener('change', function() {
  document.getElementById('contract-file-consistency-name').textContent =
    formatFileListName(this.files, '尚未选择文件');
});

document.getElementById('contract-file-main').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-main-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择主协议';
});

document.getElementById('contract-file-supplement').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-supplement-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择补充协议';
});

document.getElementById('contract-file-current').addEventListener('change', function() {
  const file = this.files && this.files[0];
  document.getElementById('contract-file-current-name').textContent = file
    ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB`
    : '尚未选择当前合同';
});

document.getElementById('contract-file-historical').addEventListener('change', function() {
  document.getElementById('contract-file-historical-name').textContent =
    formatFileListName(this.files, '尚未选择历史合同');
});

document.getElementById('contract-review-btn').addEventListener('click', async function() {
  const fileInput = document.getElementById('contract-file-input');
  const file = fileInput.files && fileInput.files[0];
  if (!file) {
    showRuntimeError('请先选择待审阅合同。');
    return;
  }

  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = 'AI 审阅中...';
  hideAllContractReports();
  document.getElementById('contract-empty-title').textContent = '正在审阅合同';
  document.getElementById('contract-empty-copy').textContent =
    '正在核验 15 大类核心条款并抽取交易信息，请稍候。';
  setRuntimeProgress(25);

  try {
    const payload = await readBpFilePayload(file, 'contract');
    payload.contractType = document.getElementById('contract-type').value;
    payload.projectName = document.getElementById('contract-project-name').value.trim();
    payload.reviewer = document.getElementById('contract-reviewer').value.trim();
    payload.reviewPhase = 'formal_review';
    if (contractDdRiskHints.length) {
      payload.ddRiskHints = contractDdRiskHints;
    }
    setRuntimeProgress(55);
    const result = await requestRuntime('/api/business/contracts/review', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    latestContractReview = result;
    renderContractReview(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('contract-empty-title').textContent = '合同审阅失败';
    document.getElementById('contract-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('contract-compare-btn').addEventListener('click', async function() {
  const baseFile = document.getElementById('contract-file-base').files?.[0];
  const targetFile = document.getElementById('contract-file-target').files?.[0];
  if (!baseFile || !targetFile) {
    showRuntimeError('请同时选择旧版与新版合同。');
    return;
  }

  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '对比中...';
  hideAllContractReports();
  document.getElementById('contract-empty-title').textContent = '正在对比合同版本';
  document.getElementById('contract-empty-copy').textContent =
    '正在识别新增、修改与删除类条款变更，请稍候。';
  setRuntimeProgress(25);

  try {
    const [base, target] = await Promise.all([
      readBpFilePayload(baseFile, 'contract'),
      readBpFilePayload(targetFile, 'contract')
    ]);
    setRuntimeProgress(55);
    const result = await requestRuntime('/api/business/contracts/compare', {
      method: 'POST',
      body: JSON.stringify({
        projectName: document.getElementById('contract-project-name').value.trim(),
        base,
        target
      })
    });
    latestContractCompare = result;
    renderContractCompare(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('contract-empty-title').textContent = '版本对比失败';
    document.getElementById('contract-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('contract-consistency-btn').addEventListener('click', async function() {
  const files = [...(document.getElementById('contract-file-consistency').files || [])];
  if (!files.length) {
    showRuntimeError('请至少选择 1 份合同。');
    return;
  }
  if (files.length > 4) {
    showRuntimeError('一致性校验一次最多 4 份合同。');
    return;
  }

  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '校验中...';
  hideAllContractReports();
  document.getElementById('contract-empty-title').textContent = '正在进行一致性校验';
  document.getElementById('contract-empty-copy').textContent =
    '正在检查内部冲突与跨合同条款一致性，请稍候。';
  setRuntimeProgress(25);

  try {
    const contracts = await Promise.all(files.map((file) => readBpFilePayload(file, 'contract')));
    setRuntimeProgress(55);
    const result = await requestRuntime('/api/business/contracts/consistency', {
      method: 'POST',
      body: JSON.stringify({
        projectName: document.getElementById('contract-project-name').value.trim(),
        contracts
      })
    });
    latestContractAdvanced = result;
    renderContractConsistency(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('contract-empty-title').textContent = '一致性校验失败';
    document.getElementById('contract-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('contract-supplement-btn').addEventListener('click', async function() {
  const mainFile = document.getElementById('contract-file-main').files?.[0];
  const supplementFile = document.getElementById('contract-file-supplement').files?.[0];
  if (!mainFile || !supplementFile) {
    showRuntimeError('请同时选择主协议与补充协议。');
    return;
  }

  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '分析中...';
  hideAllContractReports();
  document.getElementById('contract-empty-title').textContent = '正在分析补充协议影响';
  document.getElementById('contract-empty-copy').textContent =
    '正在识别补充协议对主协议条款的修改与权益影响，请稍候。';
  setRuntimeProgress(25);

  try {
    const [main, supplement] = await Promise.all([
      readBpFilePayload(mainFile, 'contract'),
      readBpFilePayload(supplementFile, 'contract')
    ]);
    setRuntimeProgress(55);
    const result = await requestRuntime('/api/business/contracts/supplement-impact', {
      method: 'POST',
      body: JSON.stringify({
        projectName: document.getElementById('contract-project-name').value.trim(),
        main,
        supplement
      })
    });
    latestContractAdvanced = result;
    renderContractSupplement(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('contract-empty-title').textContent = '补充协议影响分析失败';
    document.getElementById('contract-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('contract-history-btn').addEventListener('click', async function() {
  const currentFile = document.getElementById('contract-file-current').files?.[0];
  const historicalFiles = [...(document.getElementById('contract-file-historical').files || [])];
  if (!currentFile || !historicalFiles.length) {
    showRuntimeError('请选择当前合同，并至少 1 份历史合同。');
    return;
  }
  if (historicalFiles.length > 3) {
    showRuntimeError('历史合同一次最多 3 份。');
    return;
  }

  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '对比中...';
  hideAllContractReports();
  document.getElementById('contract-empty-title').textContent = '正在对比历史合同';
  document.getElementById('contract-empty-copy').textContent =
    '正在识别条款演变趋势与投资人权益影响，请稍候。';
  setRuntimeProgress(25);

  try {
    const [current, ...historical] = await Promise.all([
      readBpFilePayload(currentFile, 'contract'),
      ...historicalFiles.map((file) => readBpFilePayload(file, 'contract'))
    ]);
    setRuntimeProgress(55);
    const result = await requestRuntime('/api/business/contracts/history-compare', {
      method: 'POST',
      body: JSON.stringify({
        projectName: document.getElementById('contract-project-name').value.trim(),
        current,
        historical
      })
    });
    latestContractAdvanced = result;
    renderContractHistory(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('contract-empty-title').textContent = '历史合同对比失败';
    document.getElementById('contract-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('contract-clause-list').addEventListener('click', function(event) {
  const button = event.target.closest('[data-contract-clause], [data-contract-missing]');
  if (!button || !latestContractReview) return;
  this.querySelectorAll('.contract-clause-item').forEach(item => item.classList.remove('active'));
  button.classList.add('active');
  if (button.dataset.contractClause !== undefined) {
    renderContractClauseDetail(
      latestContractReview.clauses[Number(button.dataset.contractClause)]
    );
    return;
  }
  renderMissingContractClause(
    latestContractReview.missingClauses[Number(button.dataset.contractMissing)]
  );
});

document.querySelectorAll('[data-ddq-dim]').forEach((chip) => {
  chip.addEventListener('click', function() {
    this.classList.toggle('active');
  });
});

document.getElementById('ddq-file-input').addEventListener('change', function() {
  const file = this.files && this.files[0];
  if (!file) {
    document.getElementById('ddq-file-name').textContent = '尚未选择文件';
    return;
  }
  try {
    assertUploadFile(file, 'dd');
    document.getElementById('ddq-file-name').textContent =
      `${file.name} · ${(file.size / 1024).toFixed(1)} KB`;
  } catch (error) {
    this.value = '';
    document.getElementById('ddq-file-name').textContent = '尚未选择文件';
    showRuntimeError(error.message);
  }
});

document.getElementById('ddq-from-bp').addEventListener('click', function() {
  if (!latestBpParse) {
    showRuntimeError('请先完成 BP 建档解析。');
    return;
  }
  const archive = latestBpParse.archive || {};
  const fields = archive.fields || {};
  const intake = latestBpParse.intake || {};
  document.getElementById('ddq-company').value =
    fields.companyName?.value || intake.companyName || '';
  document.getElementById('ddq-industry').value =
    fields.industry?.value || intake.industryName || '';
  document.getElementById('ddq-stage').value =
    fields.financingRound?.value || intake.financingRound || '';
  document.getElementById('ddq-amount').value =
    fields.financingAmount?.value || intake.investmentAmount || '';
  document.getElementById('ddq-bp').value =
    archive.cleanedText ||
    latestBpParse.documentContent ||
    latestBpParse.fullText ||
    latestBpParse.textPreview ||
    '';
  document.getElementById('ddq-file-name').textContent =
    archive.bpFileName || latestBpParse.fileName
      ? `已带入建档文本：${archive.bpFileName || latestBpParse.fileName}`
      : '尚未选择文件';
});

document.getElementById('ddq-generate-btn').addEventListener('click', async function() {
  await runDdQuestionGenerate({ mode: 'llm', button: this });
});

document.getElementById('ddq-generate-catalog-btn').addEventListener('click', async function() {
  await runDdQuestionGenerate({ mode: 'catalog', button: this, useChecklistApi: true });
});

async function runDdQuestionGenerate(options) {
  const trigger = options.button;
  const companyName = document.getElementById('ddq-company').value.trim();
  const bpContent = document.getElementById('ddq-bp').value.trim();
  const fileInput = document.getElementById('ddq-file-input');
  const file = fileInput.files && fileInput.files[0];
  if (!companyName && !bpContent && !file) {
    showRuntimeError('请上传尽调材料文件，或填写企业名称 / 粘贴文本。');
    return;
  }
  const focusDimensions = [...document.querySelectorAll('[data-ddq-dim].active')]
    .map((item) => item.dataset.ddqDim)
    .filter(Boolean);
  if (!focusDimensions.length) {
    showRuntimeError('请至少选择一个尽调维度。');
    return;
  }

  const originalText = trigger.textContent;
  trigger.disabled = true;
  trigger.textContent = '生成中...';
  document.getElementById('ddq-report').classList.remove('active');
  document.getElementById('ddq-empty').style.display = 'flex';
  document.getElementById('ddq-empty-title').textContent =
    options.mode === 'catalog' ? '正在生成六维规则清单' : '正在生成尽调问题清单';
  document.getElementById('ddq-empty-copy').textContent = file
    ? '正在解析上传文件并生成六维问题清单，请稍候。'
    : '正在适配尽调框架并生成六维问题、资料需求与报告映射，请稍候。';
  setRuntimeProgress(25);

  try {
    const payload = {
      projectName: companyName,
      companyName,
      industry: document.getElementById('ddq-industry').value.trim(),
      financingStage: document.getElementById('ddq-stage').value.trim(),
      financingAmount: document.getElementById('ddq-amount').value.trim(),
      scenario: document.getElementById('ddq-scenario').value,
      focusDimensions,
      bpContent,
      mode: options.mode === 'catalog' ? 'catalog' : undefined
    };
    if (currentProjectId && currentProjectId !== 'draft') {
      payload.projectId = currentProjectId;
    }
    if (file) {
      const filePayload = await readBpFilePayload(file, 'dd');
      Object.assign(payload, filePayload);
      if (filePayload.textContent && bpContent) {
        payload.bpContent = [
          `【上传文件：${file.name}】`,
          filePayload.textContent,
          '',
          '【补充说明】',
          bpContent
        ].join('\n');
        delete payload.contentBase64;
      } else if (filePayload.textContent) {
        payload.bpContent = filePayload.textContent;
        delete payload.contentBase64;
      }
    }
    setRuntimeProgress(55);
    const endpoint = options.useChecklistApi
      ? '/api/business/dd-checklist/generate'
      : '/api/business/dd-questions/generate';
    const body = options.useChecklistApi
      ? { ...payload, mode: options.mode || 'catalog' }
      : payload;
    const result = await requestRuntime(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
    latestDdQuestions = result.checklist || result;
    renderDdQuestions(latestDdQuestions);
    if (result.closures?.items) {
      ddChecklistStore[currentProjectId] = {};
      result.closures.items.forEach((item) => {
        ddChecklistStore[currentProjectId][item.itemKey] = item;
      });
      renderDdClosureList(currentProjectId);
    } else if (currentProjectId && currentProjectId !== 'draft') {
      await loadDdChecklistClosures(currentProjectId);
    }
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('ddq-empty-title').textContent = '尽调问题生成失败';
    document.getElementById('ddq-empty-copy').textContent = error.message;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    trigger.disabled = false;
    trigger.textContent = originalText;
  }
}

document.getElementById('ddq-dim-list').addEventListener('click', function(event) {
  const button = event.target.closest('[data-ddq-view-dim]');
  if (!button || !latestDdQuestions) return;
  ddqActiveDimension = button.dataset.ddqViewDim;
  renderDdQuestionDimension(latestDdQuestions, ddqActiveDimension);
});

document.getElementById('bp-file-input').addEventListener('change', function() {
  const file = this.files && this.files[0];
  if (!file) {
    document.getElementById('bp-file-label').textContent = '未选择文件';
    return;
  }
  try {
    assertUploadFile(file, 'bp');
    document.getElementById('bp-file-label').textContent =
      `${file.name} · ${(file.size / 1024).toFixed(1)} KB`;
  } catch (error) {
    this.value = '';
    document.getElementById('bp-file-label').textContent = '未选择文件';
    showRuntimeError(error.message);
  }
});

async function parseBpStream(payload, onStage) {
  const whoami = getRuntimeWhoami();
  if (whoami.role && !canWriteBusiness()) {
    throw new Error('当前角色为只读（viewer），无法执行 BP 解析。');
  }
  const apiKey = getRuntimeApiKey();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 600000);
  try {
    const response = await fetch('/api/business/bp/parse-stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'X-Api-Key': apiKey } : {})
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || payload.error || payload.errorCode || 'BP 解析请求失败');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        const json = line.slice(line.indexOf(':') + 1).trim();
        if (!json) continue;
        const stage = JSON.parse(json);
        onStage(stage);
        if (stage.stage === 'complete') {
          finalResult = stage.result;
        }
        if (stage.stage === 'error') {
          throw new Error(stage.message || 'BP 解析失败');
        }
      }
    }
    if (buffer.trim()) {
      const line = buffer.trim();
      if (line.startsWith('data:')) {
        const json = line.slice(line.indexOf(':') + 1).trim();
        const stage = JSON.parse(json);
        onStage(stage);
        if (stage.stage === 'complete') finalResult = stage.result;
        if (stage.stage === 'error') throw new Error(stage.message || 'BP 解析失败');
      }
    }
    if (!finalResult) {
      throw new Error('BP 解析流异常结束，未收到完整结果。');
    }
    return finalResult;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('BP 解析流式请求超时（超过 600 秒）。请稍后重试；可缩小材料后再试。');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

const BP_PARSE_STAGE_LABELS = {
  started: '开始解析 BP',
  text_ready: 'BP 文本清洗完成',
  priority_fields: '优先字段抽取中',
  optional_fields: '补充字段抽取中',
  generated_content: '生成摘要与风险中',
  complete: '解析完成'
};

const BP_PARSE_STAGE_PROGRESS = {
  started: 10,
  text_ready: 25,
  priority_fields: 45,
  optional_fields: 65,
  generated_content: 85,
  complete: 100
};

document.getElementById('parse-bp-btn').addEventListener('click', async function() {
  const fileInput = document.getElementById('bp-file-input');
  const file = fileInput.files && fileInput.files[0];
  if (!file) {
    showRuntimeError('请先选择 BP 文件。');
    return;
  }
  this.disabled = true;
  const originalText = this.textContent;
  this.textContent = '读取文件中...';
  try {
    const payload = await readBpFilePayload(file, 'bp');
    await startBpParse(payload);
  } catch (error) {
    showRuntimeError(error.message);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

async function startBpParse(payload) {
  const p1rError = document.getElementById('bp-result-error');
  if (p1rError) p1rError.style.display = 'none';
  const p1Result = document.getElementById('p1-parse-result');
  if (p1Result) {
    p1Result.style.display = 'flex';
    // 重置所有子区域为隐藏，只保留状态文案
    ['p1-parse-header', 'p1-parse-fields', 'p1-parse-summary', 'p1-parse-highlights', 'p1-parse-risks', 'p1-parse-supplements', 'p1-parse-warnings'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });
    const statusEl = document.getElementById('p1-parse-status');
    if (statusEl) statusEl.textContent = '正在解析 BP，请稍候…';
  }
  const parseBtn = document.getElementById('bp-reparse-btn');
  if (parseBtn) {
    parseBtn.disabled = true;
    parseBtn.textContent = '解析中...';
  }
  setRuntimeProgress(5);
  try {
    const result = await parseBpStream(payload, (stage) => {
      const label = BP_PARSE_STAGE_LABELS[stage.stage] || stage.message || stage.stage;
      const progress = BP_PARSE_STAGE_PROGRESS[stage.stage] ?? 50;
      setRuntimeProgress(progress);
      const statusEl = document.getElementById('p1-parse-status');
      if (statusEl) statusEl.textContent = label;
      if (stage.stage === 'priority_fields' || stage.stage === 'optional_fields') {
        const partialArchive = {
          ...(latestBpParse?.archive || {}),
          fields: { ...(latestBpParse?.archive?.fields || {}), ...(stage.fields || {}) },
          parseStatus: 'partial'
        };
        latestBpParse = { ...(latestBpParse || {}), archive: partialArchive };
        updateP1ParseResult(latestBpParse);
      }
      if (stage.stage === 'generated_content') {
        const partialArchive = {
          ...(latestBpParse?.archive || {}),
          generatedContent: stage.generatedContent || {}
        };
        latestBpParse = { ...(latestBpParse || {}), archive: partialArchive };
        updateP1ParseResult(latestBpParse);
      }
      if (stage.stage === 'complete') {
        latestBpParse = stage.result;
        updateP1ParseResult(latestBpParse);
      }
    });
    if (result) {
      latestBpParse = result;
      updateP1ParseResult(result);
    }
    const statusEl = document.getElementById('p1-parse-status');
    if (statusEl) statusEl.textContent = '解析完成，可点击下方按钮查看草稿或确认立项。';
    setRuntimeProgress(100);
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
    const statusEl = document.getElementById('p1-parse-status');
    if (statusEl) statusEl.textContent = '解析失败，请重试。';
  } finally {
    if (parseBtn) {
      parseBtn.disabled = false;
      parseBtn.textContent = '重新解析';
    }
  }
}

function updateP1ParseFields(fields) {
  const container = document.getElementById('p1-parse-fields');
  const grid = document.getElementById('p1-parse-fields-grid');
  if (!container || !grid) return;
  const fieldLabels = {
    projectName: '项目名称', companyName: '企业名称', companyShortName: '企业简称',
    industry: '所属行业', subSector: '细分赛道', region: '所在地区', foundedAt: '成立时间',
    founders: '创始人', coreTeam: '核心团队', financingRound: '融资轮次',
    financingAmount: '融资金额', preMoneyValuation: '投前估值', postMoneyValuation: '投后估值',
    useOfProceeds: '募资用途', mainBusiness: '主营业务', businessModel: '商业模式',
    coreProducts: '核心产品', financialSummary: '财务摘要', revenue: '收入',
    grossMargin: '毛利率', profit: '利润', cashFlow: '现金流',
    competitiveAdvantage: '竞争优势', keyCustomers: '主要客户', targetCustomers: '目标客户',
    shareholderStructure: '股东结构', fundingHistory: '历史融资', registeredCapital: '注册资本',
    legalRepresentative: '法定代表人', unifiedSocialCreditCode: '信用代码',
    contactPerson: '联系人', contactInfo: '联系方式', equityOffered: '出让股权'
  };
  const orderedKeys = Object.keys(fieldLabels).filter((key) => fields?.[key]?.value);
  grid.innerHTML = orderedKeys.length
    ? orderedKeys.map((key) => {
        const field = fields[key];
        return `<div style="background:#F9FAFB;padding:6px 8px;border-radius:4px;border:1px solid #E5E7EB;min-width:0;">
          <div style="font-size:10px;color:#6B7280;margin-bottom:2px;">${escapeHtml(fieldLabels[key] || key)}</div>
          <div style="font-size:11px;color:#0B1426;font-weight:500;line-height:1.4;word-break:break-word;overflow-wrap:anywhere;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;" title="${escapeHtml(field.value)}">${escapeHtml(field.value)}</div>
        </div>`;
      }).join('')
    : '<div style="color:#9CA3AF;grid-column:1/-1;">暂无字段</div>';
  container.style.display = 'block';
}

function updateP1ParseSummary(generatedContent) {
  const container = document.getElementById('p1-parse-summary');
  const textEl = document.getElementById('p1-parse-summary-text');
  if (!container || !textEl) return;
  const summary = generatedContent?.projectSummary || '--';
  textEl.textContent = summary;
  container.style.display = summary === '--' ? 'none' : 'block';
}

function updateP1ParseHighlights(highlights) {
  const container = document.getElementById('p1-parse-highlights');
  const list = document.getElementById('p1-parse-highlights-list');
  if (!container || !list) return;
  const items = (highlights || []).filter((h) => h && (h.content || typeof h === 'string'));
  list.innerHTML = items.length
    ? items.map((item) => {
        const content = typeof item === 'string' ? item : item.content;
        const needConfirm = typeof item === 'object' && item.needConfirm ? ' <span style="color:#F97316;">(待确认)</span>' : '';
        return `<li>${escapeHtml(content)}${needConfirm}</li>`;
      }).join('')
    : '<li>暂无</li>';
  container.style.display = 'block';
}

function updateP1ParseRisks(risks) {
  const container = document.getElementById('p1-parse-risks');
  const list = document.getElementById('p1-parse-risks-list');
  if (!container || !list) return;
  const items = (risks || []).filter((r) => r && (r.content || typeof r === 'string'));
  list.innerHTML = items.length
    ? items.map((item) => {
        const content = typeof item === 'string' ? item : item.content;
        const level = typeof item === 'object' && item.riskLevel ? `[${escapeHtml(item.riskLevel)}] ` : '';
        return `<li>${level}${escapeHtml(content)}</li>`;
      }).join('')
    : '<li>暂无</li>';
  container.style.display = 'block';
}

function updateP1ParseSupplements(supplements) {
  const container = document.getElementById('p1-parse-supplements');
  const textEl = document.getElementById('p1-parse-supplements-text');
  if (!container || !textEl) return;
  const items = (supplements || []).filter(Boolean);
  textEl.textContent = items.length ? items.join('、') : '暂无';
  container.style.display = 'block';
}

function updateP1ParseWarnings(warnings) {
  const container = document.getElementById('p1-parse-warnings');
  const list = document.getElementById('p1-parse-warnings-list');
  if (!container || !list) return;
  const items = (warnings || []).filter(Boolean);
  if (!items.length) {
    container.style.display = 'none';
    return;
  }
  list.innerHTML = items.map((w) => `<div style="margin-bottom:4px;">· ${escapeHtml(String(w))}</div>`).join('');
  container.style.display = 'block';
}

function updateP1ParseHeader(result) {
  const header = document.getElementById('p1-parse-header');
  const nameEl = document.getElementById('p1-parse-project-name');
  const infoEl = document.getElementById('p1-parse-company-info');
  if (!header || !nameEl || !infoEl) return;
  const fields = result?.archive?.fields || {};
  const projectName = fields.projectName?.value || fields.companyName?.value || '未命名项目';
  const companyName = fields.companyName?.value || '未识别企业';
  const industry = fields.industry?.value || '--';
  const round = fields.financingRound?.value || '--';
  const region = fields.region?.value || '--';
  nameEl.textContent = projectName;
  infoEl.textContent = `${companyName} · ${industry} · ${round} · ${region}`;
  header.style.display = 'block';
}

function updateP1ParseResult(result) {
  const archive = result?.archive || {};
  updateP1ParseHeader(result);
  updateP1ParseFields(archive.fields);
  updateP1ParseSummary(archive.generatedContent);
  updateP1ParseHighlights(archive.generatedContent?.investmentHighlights);
  updateP1ParseRisks(archive.generatedContent?.riskPoints);
  updateP1ParseSupplements(archive.generatedContent?.supplementMaterials);
  updateP1ParseWarnings(archive.warnings);
  renderBpParseResult(result);
}

document.getElementById('bp-confirm-project-btn').addEventListener('click', async function() {
  await createProjectFromParsedBp(this);
});

document.getElementById('p1-view-draft-btn').addEventListener('click', function() {
  navigatePortalPage('p1r');
});

document.getElementById('p1-confirm-project-btn').addEventListener('click', async function() {
  await createProjectFromParsedBp(this);
});

document.getElementById('bp-external-fill-btn').addEventListener('click', async function() {
  await fillBpFieldsFromExternalData(this);
});

document.getElementById('bp-reparse-btn').addEventListener('click', function() {
  if (!latestBpParse?.fileName && !latestBpParse?.archive?.bpFileName) {
    showRuntimeError('没有可重新解析的 BP 文件。');
    return;
  }
  const fileName = latestBpParse?.fileName || latestBpParse?.archive?.bpFileName || 'BP';
  const textContent = latestBpParse?.documentContent || latestBpParse?.fullText || latestBpParse?.textPreview || '';
  if (!textContent) {
    showRuntimeError('缺少 BP 文本，无法重新解析。');
    return;
  }
  const payload = { fileName, textContent };
  void startBpParse(payload);
});

document.getElementById('bp-save-draft-btn').addEventListener('click', function() {
  if (!latestBpParse?.archive) {
    showRuntimeError('暂无解析结果可保存。');
    return;
  }
  renderBpParseResult(latestBpParse);
  showRuntimeError('草稿已保存（本地缓存），可继续编辑或确认立项。');
});

document.getElementById('bp-back-to-intake').addEventListener('click', function() {
  navigatePortalPage('p1');
});

document.getElementById('bp-start-prejudge').addEventListener('click', function() {
  if (!latestBpParse?.archive) {
    showRuntimeError('请先完成 BP 建档解析。');
    return;
  }
  navigatePortalPage('p1s');
  // 导航到 p1s 后由 onPageEnter 触发数据检查，避免重复调用
});

document.getElementById('prejudge-back').addEventListener('click', function() {
  navigatePortalPage('p1r');
});

document.getElementById('prejudge-rerun').addEventListener('click', async function() {
  await runPreJudgment(this);
});

document.getElementById('prejudge-check-start').addEventListener('click', async function() {
  if (!latestBpParse?.archive) {
    showPrejudgeCheckMessage('请先完成 BP 建档解析。', 'error');
    return;
  }
  await runPreJudgment(this);
});

document.getElementById('prejudge-match-evidence').addEventListener('click', async function() {
  await matchPreJudgmentEvidence(this);
});

document.getElementById('prejudge-check-external').addEventListener('click', async function() {
  if (!latestBpParse?.archive?.fields) {
    showPrejudgeCheckMessage('请先完成 BP 建档解析。', 'error');
    return;
  }
  await fillBpFieldsFromExternalData(this);
  await loadPreJudgmentCheck();
});

document.getElementById('prejudge-check-edit').addEventListener('click', function() {
  navigatePortalPage('p1r');
});

document.getElementById('prejudge-confirm-review').addEventListener('click', async function() {
  if (!latestPreJudgment) {
    showRuntimeError('请先完成前置研判评分。');
    return;
  }
  await createProjectFromParsedBp(this);
});

document.getElementById('refresh-project-list').addEventListener('click', () => {
  void loadProjectList();
});

document.getElementById('project-list').addEventListener('click', async (event) => {
  const target = event.target.closest('[data-open-project]');
  if (!target) return;
  const projectId = target.getAttribute('data-open-project');
  if (!projectId) return;
  target.textContent = '打开中...';
  try {
    await openExistingProject(projectId);
    navigatePortalPage('p2');
  } catch (error) {
    showRuntimeError(error.message);
  } finally {
    await loadProjectList();
  }
});

document.getElementById('wb-recent-projects')?.addEventListener('click', async (event) => {
  const target = event.target.closest('[data-open-project]');
  if (!target) return;
  const projectId = target.getAttribute('data-open-project');
  if (!projectId) return;
  try {
    await openExistingProject(projectId);
    navigatePortalPage('p2');
  } catch (error) {
    showRuntimeError(error.message);
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-resume-run]');
  if (!button) return;
  // 仅处理带有效 runId 的续跑按钮（含审批「确认并续跑」）
  const runId = button.getAttribute('data-resume-run');
  if (!runId) return;
  if (getRuntimeWhoami().role && !canWriteBusiness()) {
    showRuntimeError('当前角色为只读（viewer），无法断点续跑。');
    return;
  }
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '恢复中...';
  try {
    const result = await requestRuntime(
      `/api/business/workflow-runs/${encodeURIComponent(runId)}/resume`,
      { method: 'POST' }
    );
    renderReviewPage(result, currentProjectInput);
    renderRuntimeResult(result);
    updateStageProjectLabels(result);
    const stagePage = mapWorkflowToStagePage(
      (result.workflowId || '').replace(/^phase1_/, '') || result.stage
    );
    if (stagePage) {
      renderStageResult(stagePage, result);
    }
    if (result.stage === 'investment_committee' || stagePage === 'p6') {
      renderIcDecisionResult(result);
    }
    if (result.stage === 'exit' || stagePage === 'p9') {
      renderExitWorkflowResult(result);
    }
    await loadProjectList();
    document.getElementById('review-summary').textContent = result.awaitingApproval
      ? '仍在等待人工审批；请再次确认后续跑。'
      : (result.resumed
        ? '已确认并续跑完成。'
        : formatDecision(result.decision));
  } catch (error) {
    showRuntimeError(`断点续跑失败：${error.message}`);
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
});

document.querySelectorAll('[data-workflow]').forEach(button => {
  button.addEventListener('click', async function() {
    if (!currentProjectId) {
      document.querySelector('[data-page="p1"]').click();
      showRuntimeError('请先创建项目并完成初评。');
      return;
    }
    const workflow = this.dataset.workflow;
    const stagePage = this.dataset.stagePage;
    const originalText = this.textContent;
    this.disabled = true;
    this.textContent = 'Runtime 执行中...';
    try {
      const result = await requestRuntime(
        `/api/business/projects/${encodeURIComponent(currentProjectId)}/workflows/${workflow}`,
        { method: 'POST' }
      );
      renderReviewPage(result, currentProjectInput);
      renderStageResult(stagePage || mapWorkflowToStagePage(workflow), result);
      updateStageProjectLabels(result);
      if (workflow === 'investment_committee' || stagePage === 'p6') {
        renderIcDecisionResult(result);
      }
      if (workflow === 'exit' || stagePage === 'p9') {
        renderExitWorkflowResult(result);
      }
      await loadProjectList();
      if (stagePage) {
        document.querySelector(`[data-page="${stagePage}"]`)?.click();
      }
    } catch (error) {
      const message = `Workflow 执行失败：${error.message}`;
      document.getElementById('review-summary').textContent = message;
      if (stagePage) {
        const panel = document.getElementById(`stage-result-${stagePage}`);
        if (panel) panel.textContent = message;
      }
    } finally {
      this.disabled = false;
      this.textContent = originalText;
    }
  });
});

async function runProjectSkill(skillName, options = {}) {
  if (!currentProjectId) {
    document.querySelector('[data-page="p1"]').click();
    showRuntimeError('请先创建项目并完成初评。');
    throw new Error('请先创建项目并完成初评。');
  }
  return requestRuntime(
    `/api/business/projects/${encodeURIComponent(currentProjectId)}/skills/${encodeURIComponent(skillName)}`,
    {
      method: 'POST',
      body: JSON.stringify(options.body || {})
    }
  );
}

function formatFinanceValue(value, suffix = '') {
  if (value === undefined || value === null || value === '') return '--';
  return `${value}${suffix}`;
}

function renderValuationSkillResult(result) {
  const output = result.output || {};
  const finance = result.context?.finance || {};
  const company = result.context?.company || {};
  document.getElementById('valuation-project-label').textContent =
    company.companyName || currentProjectInput.companyName || currentProjectId || '当前项目';
  document.getElementById('valuation-revenue').innerHTML =
    `<strong>${escapeHtml(formatFinanceValue(finance.revenue))}</strong>`;
  document.getElementById('valuation-margin').innerHTML =
    `<strong>${escapeHtml(formatFinanceValue(finance.grossMargin, '%'))}</strong>`;
  document.getElementById('valuation-profit').innerHTML =
    `<strong>${escapeHtml(formatFinanceValue(finance.netProfit))}</strong>`;
  document.getElementById('valuation-cash').innerHTML =
    `<strong>${escapeHtml(formatFinanceValue(finance.cashFlow))}</strong>`;
  document.getElementById('valuation-range').textContent =
    output.valuationRange || '未返回区间';
  document.getElementById('valuation-method').textContent =
    output.method || '--';
  document.getElementById('valuation-commentary').textContent =
    output.commentary || '暂无说明';
  const evidence = (result.context?.evidence || []).filter(
    (item) => item.source === 'valuation_analysis'
  );
  document.getElementById('valuation-evidence').innerHTML = evidence.length
    ? evidence.map((item) => `<li>${escapeHtml(item.description)}</li>`).join('')
    : '<li>暂无估值证据写入</li>';
  const panel = document.getElementById('stage-result-p4');
  panel.innerHTML = `
    <strong>ValuationSkill</strong>
    · runId <code style="font-size:10px;">${escapeHtml(result.runId || '')}</code>
    · ${result.durationMs || 0} ms
    <div style="margin-top:6px;">方法：${escapeHtml(output.method || '--')} · 区间：${escapeHtml(output.valuationRange || '--')}</div>`;
}

document.getElementById('valuation-skill-btn').addEventListener('click', async function() {
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '估值中...';
  setRuntimeProgress(30);
  try {
    const result = await runProjectSkill('ValuationSkill');
    renderValuationSkillResult(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('stage-result-p4').textContent = `估值失败：${error.message}`;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

/** 从 BP 建档草稿/项目输入中提取估值模型数值输入（万元、%） */
function collectValuationModelInput() {
  const fields = latestBpParse?.archive?.fields || {};
  const pickText = (key) => {
    const raw = fields[key]?.value;
    return raw === undefined || raw === null ? '' : String(raw).trim();
  };
  const pickNum = (key) => {
    const text = pickText(key).replace(/[,，]/g, '');
    if (!text) return undefined;
    // 支持「5000万」「1.2亿」「3000万元」等中文金额表述
    const yi = text.match(/(\d+(?:\.\d+)?)\s*亿/);
    if (yi) return Number(yi[1]) * 10000;
    const wan = text.match(/(\d+(?:\.\d+)?)\s*万/);
    if (wan) return Number(wan[1]);
    const plain = text.match(/(\d+(?:\.\d+)?)/);
    return plain ? Number(plain[1]) : undefined;
  };
  const pickPct = (key) => {
    const text = pickText(key).replace(/[％%]/, '');
    const match = text.match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : undefined;
  };
  const revenue = pickNum('revenue');
  const netProfit = pickNum('profit');
  const grossMargin = pickPct('grossMargin');
  const financingAmount = pickNum('financingAmount');
  const equityOffered = pickPct('equityOffered');
  const industry = pickText('industry') || currentProjectInput.industryName || '';
  const financingRound = pickText('financingRound') || currentProjectInput.financingRound || '';
  return {
    revenue,
    netProfit,
    grossMargin,
    financingAmount,
    equityOffered,
    industry: industry || undefined,
    financingRound: financingRound || undefined,
  };
}

function renderValuationModelResult(result) {
  const panel = document.getElementById('valuation-model-panel');
  if (panel) panel.style.display = 'block';
  const compositeEl = document.getElementById('valuation-model-composite');
  if (compositeEl) {
    compositeEl.textContent = result.composite
      ? `${result.composite.low.toLocaleString()} – ${result.composite.high.toLocaleString()}`
      : '数据不足';
  }
  const notesEl = document.getElementById('valuation-model-notes');
  if (notesEl) {
    notesEl.innerHTML = (result.notes || []).map((n) => `<div>· ${escapeHtml(n)}</div>`).join('');
  }
  const tbody = document.getElementById('valuation-model-tbody');
  if (tbody) {
    tbody.innerHTML = (result.methods || []).map((m) => {
      const range = m.applicable
        ? `${(m.valueLow ?? 0).toLocaleString()} – ${(m.valueHigh ?? 0).toLocaleString()}`
        : `<span style="color:#9CA3AF;">不适用：${escapeHtml(m.reason || '')}</span>`;
      const assumptions = (m.assumptions || []).map((a) => `<div>· ${escapeHtml(a)}</div>`).join('');
      return `<tr style="border-bottom:1px solid #F3F4F6;${m.applicable ? '' : 'opacity:0.6;'}">
        <td style="padding:6px 8px;white-space:nowrap;">${escapeHtml(m.method)}</td>
        <td style="padding:6px 8px;white-space:nowrap;">${range}</td>
        <td style="padding:6px 8px;">${m.applicable ? `${Math.round(m.weight * 100)}%` : '--'}</td>
        <td style="padding:6px 8px;color:#4B5563;line-height:1.5;">${assumptions || '--'}</td>
      </tr>`;
    }).join('');
  }
  const disclaimerEl = document.getElementById('valuation-model-disclaimer');
  if (disclaimerEl && result.disclaimer) {
    disclaimerEl.style.display = 'block';
    disclaimerEl.textContent = result.disclaimer;
  }
  const stagePanel = document.getElementById('stage-result-p4');
  if (stagePanel) {
    stagePanel.innerHTML = `<strong>专业估值模型</strong> · 加权区间：${escapeHtml(
      result.composite ? `${result.composite.low.toLocaleString()} – ${result.composite.high.toLocaleString()} 万元` : '数据不足'
    )}`;
  }
}

function renderFinModelResult(result) {
  const panel = document.getElementById('fin-model-panel');
  if (panel) panel.style.display = 'block';
  const ev = document.getElementById('fin-model-ev');
  const equity = document.getElementById('fin-model-equity');
  const balance = document.getElementById('fin-model-balance');
  const status = document.getElementById('fin-model-assumption-status');
  const notes = document.getElementById('fin-model-notes');
  if (ev) ev.textContent = result?.dcf?.enterpriseValue?.toLocaleString?.() ?? '--';
  if (equity) equity.textContent = result?.dcf?.equityValue?.toLocaleString?.() ?? '--';
  if (balance) {
    balance.textContent = result?.checks?.balanceSheetBalanced ? '通过' : '失败';
    balance.style.color = result?.checks?.balanceSheetBalanced ? '#059669' : '#DC2626';
  }
  if (status) status.textContent = result?.assumptions?.status ?? 'draft';
  if (notes) {
    notes.textContent = (result?.checks?.messages || []).join(' · ');
  }
  const stagePanel = document.getElementById('stage-result-p4');
  if (stagePanel) {
    stagePanel.innerHTML = `<strong>财务模型</strong> · EV ${escapeHtml(String(result?.dcf?.enterpriseValue ?? '--'))} 万元 · 配平 ${result?.checks?.balanceSheetBalanced ? '通过' : '失败'}`;
  }
}

document.getElementById('fin-model-run-btn')?.addEventListener('click', async function() {
  if (!currentProjectId) {
    document.getElementById('stage-result-p4').textContent = '请先选择或创建项目后再运行财务模型。';
    return;
  }
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '建模中...';
  setRuntimeProgress(20);
  try {
    const output = await requestRuntime('/api/business/financial-model/run', {
      method: 'POST',
      body: JSON.stringify({ projectId: currentProjectId }),
      timeoutMs: 60000,
    });
    renderFinModelResult(output.result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('stage-result-p4').textContent = `财务模型失败：${error.message}`;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('fin-model-export-btn')?.addEventListener('click', async function() {
  if (!currentProjectId) {
    document.getElementById('stage-result-p4').textContent = '请先选择项目后再导出 Excel。';
    return;
  }
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '导出中...';
  try {
    const output = await requestRuntime('/api/business/financial-model/export-excel', {
      method: 'POST',
      body: JSON.stringify({ projectId: currentProjectId }),
      timeoutMs: 90000,
    });
    const url = output.downloadUrl?.startsWith('http')
      ? output.downloadUrl
      : `${runtimeBaseUrl}${output.downloadUrl}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    document.getElementById('stage-result-p4').textContent = output.isDraft
      ? `已导出 DRAFT Excel（假设未确认）：${output.artifactId}`
      : `已导出 Excel：${output.artifactId}`;
  } catch (error) {
    document.getElementById('stage-result-p4').textContent = `Excel 导出失败：${error.message}`;
    showRuntimeError(error.message);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('valuation-model-btn').addEventListener('click', async function() {
  const input = collectValuationModelInput();
  const hasAnyNumber = [input.revenue, input.netProfit, input.financingAmount].some((v) => Number.isFinite(v));
  if (!hasAnyNumber) {
    document.getElementById('stage-result-p4').textContent =
      '估值模型需要至少一项数值输入（营收 / 净利润 / 本轮融资金额）。请先在 BP 建档草稿中补齐财务字段。';
    return;
  }
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '模型测算中...';
  setRuntimeProgress(30);
  try {
    const result = await requestRuntime('/api/business/valuation/estimate', {
      method: 'POST',
      body: JSON.stringify(input),
      timeoutMs: 30000,
    });
    renderValuationModelResult(result);
    setRuntimeProgress(100);
  } catch (error) {
    document.getElementById('stage-result-p4').textContent = `估值模型失败：${error.message}`;
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});


document.getElementById('ddq-skill-btn').addEventListener('click', async function() {
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = 'Skill 执行中...';
  setRuntimeProgress(25);
  try {
    const result = await runProjectSkill('DdQuestionSkill');
    latestDdQuestions = result.output;
    if (typeof renderDdQuestions === 'function' && result.output) {
      renderDdQuestions(result.output);
    }
    document.getElementById('ddq-empty').style.display = 'none';
    document.getElementById('ddq-report')?.classList.add('active');
    setRuntimeProgress(100);
    showRuntimeError(`已写入项目上下文：${result.skillName} · ${result.durationMs || 0}ms`);
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('ddq-checklist-skill-btn').addEventListener('click', async function() {
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = 'Skill 执行中...';
  setRuntimeProgress(25);
  try {
    const result = await runProjectSkill('DdChecklistSkill');
    latestDdQuestions = result.output?.checklist || result.output;
    if (typeof renderDdQuestions === 'function' && latestDdQuestions) {
      renderDdQuestions(latestDdQuestions);
    }
    if (result.output?.closures?.items && currentProjectId) {
      ddChecklistStore[currentProjectId] = {};
      result.output.closures.items.forEach((item) => {
        ddChecklistStore[currentProjectId][item.itemKey] = item;
      });
      renderDdClosureList(currentProjectId);
    }
    document.getElementById('ddq-empty').style.display = 'none';
    document.getElementById('ddq-report')?.classList.add('active');
    switchDdQuestionTab('closure');
    setRuntimeProgress(100);
    showRuntimeError(`已写入项目上下文：${result.skillName} · ${result.durationMs || 0}ms`);
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.querySelectorAll('[data-ddq-tab]').forEach((tab) => {
  tab.addEventListener('click', function() {
    switchDdQuestionTab(this.dataset.ddqTab);
  });
});

document.getElementById('ddq-closure-refresh')?.addEventListener('click', async function() {
  if (!currentProjectId || currentProjectId === 'draft') {
    showRuntimeError('请先打开或创建项目');
    return;
  }
  await loadDdChecklistClosures(currentProjectId);
  renderDdClosureList(currentProjectId);
});

document.getElementById('ddq-closure-seed')?.addEventListener('click', async function() {
  if (!currentProjectId || currentProjectId === 'draft') {
    showRuntimeError('请先打开或创建项目');
    return;
  }
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = '初始化中...';
  try {
    await requestRuntime('/api/business/dd-checklist/generate', {
      method: 'POST',
      body: JSON.stringify({
        projectId: currentProjectId,
        mode: 'catalog',
        companyName: document.getElementById('ddq-company').value.trim()
      })
    });
    await loadDdChecklistClosures(currentProjectId);
    renderDdClosureList(currentProjectId);
    switchDdQuestionTab('closure');
  } catch (error) {
    showRuntimeError(error.message);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

document.getElementById('ddq-closure-list')?.addEventListener('change', async function(event) {
  const select = event.target.closest('[data-dd-closure-status]');
  if (!select || !currentProjectId) return;
  const itemKey = select.dataset.ddClosureStatus;
  try {
    const item = await upsertDdClosureRecord(currentProjectId, itemKey, {
      status: select.value,
      owner: select.dataset.owner || '投资经理'
    });
    cacheDdClosureRecord(item);
    renderDdClosureList(currentProjectId);
  } catch (error) {
    showRuntimeError(error.message);
  }
});

document.getElementById('contract-skill-btn').addEventListener('click', async function() {
  if (contractMode !== 'review') {
    showRuntimeError('请切换到「单合同审阅」模式后再写入项目。');
    return;
  }
  if (!currentProjectId) {
    showRuntimeError('请先创建或打开项目。');
    return;
  }
  const file = document.getElementById('contract-file-input').files?.[0];
  const originalText = this.textContent;
  this.disabled = true;
  this.textContent = 'Skill 审阅中...';
  setRuntimeProgress(25);
  try {
    let body = {};
    if (file) {
      const payload = await readBpFilePayload(file, 'contract');
      const content = payload.textContent
        ? payload.textContent
        : payload.contentBase64
          ? `__BASE64__:${payload.contentBase64}`
          : '';
      if (!content) {
        throw new Error('无法读取合同文件内容。');
      }
      body = {
        document: {
          name: payload.fileName || file.name,
          type: 'contract',
          content,
          source: 'business-portal://contract-skill'
        }
      };
    }
    const result = await runProjectSkill('ContractReviewSkill', { body });
    if (result.output) {
      latestContractReview = result.output;
      renderContractReview(result.output);
    }
    setRuntimeProgress(100);
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    this.disabled = false;
    this.textContent = originalText;
  }
});

function mapWorkflowToStagePage(workflow) {
  if (workflow === 'due_diligence') return 'p3';
  if (workflow === 'investment_committee') return 'p6';
  if (workflow === 'post_investment') return 'p8';
  if (workflow === 'exit') return 'p9';
  return 'p2';
}

/** 是否处于人工审批暂停（pending_approval / awaitingApproval） */

/** 生成审批横幅 HTML（确认并续跑 → POST .../resume） */

/** 更新 p2 固定审批横幅显隐与 runId */

function renderIcDecisionResult(result) {
  const name = result.company?.companyName || currentProjectInput.companyName || result.projectId || '当前项目';
  const decision = result.decision || {};
  const reasons = Array.isArray(decision.reasons) ? decision.reasons : [];
  const actions = Array.isArray(decision.nextActions) ? decision.nextActions : [];
  const risks = Array.isArray(result.risks) ? result.risks : [];
  document.getElementById('ic-decision-project').innerHTML = `<strong>${escapeHtml(name)}</strong>`;
  document.getElementById('ic-decision-result').textContent = decisionLabel(decision.result);
  document.getElementById('ic-decision-risks').textContent = String(risks.length);
  document.getElementById('ic-decision-title').textContent = `AI 投决备忘录 — ${name}`;
  document.getElementById('ic-decision-badge').textContent = decisionLabel(decision.result);
  document.getElementById('ic-decision-reasons').innerHTML = reasons.length
    ? reasons.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无理由</li>';
  document.getElementById('ic-decision-actions').innerHTML = actions.length
    ? actions.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无行动项</li>';
  document.getElementById('ic-decision-risk-list').innerHTML = risks.length
    ? risks.slice(0, 6).map((item) => `<li>[${escapeHtml(item.severity || 'medium')}] ${escapeHtml(item.description || '')}</li>`).join('')
    : '<li>暂无结构化风险</li>';
}

function buildIcMaterialsPayload(reportTypeOverride) {
  const companyName =
    currentProjectInput.companyName ||
    document.getElementById('ic-project-name')?.textContent?.trim() ||
    '';
  const bpSummary = document.getElementById('icm-bp').value.trim();
  const ddSummary = document.getElementById('icm-dd').value.trim();
  const contractSummary = document.getElementById('icm-contract').value.trim();
  const riskNotes = document.getElementById('icm-risks').value.trim();
  const meetingDate = document.getElementById('icm-meeting-date')?.value?.trim() || '';
  const meetingTitle = document.getElementById('icm-meeting-title')?.value?.trim() || '';
  const attendees = document.getElementById('icm-attendees')?.value?.trim() || '';
  const discussionNotes = document.getElementById('icm-discussion')?.value?.trim() || '';
  const resolutions = document.getElementById('icm-resolutions')?.value?.trim() || '';
  const actionItems = document.getElementById('icm-actions')?.value?.trim() || '';
  return {
    projectId: currentProjectId || undefined,
    projectName: companyName || undefined,
    companyName: companyName || undefined,
    industry: currentProjectInput.industryName || currentProjectInput.industry || undefined,
    financingStage: currentProjectInput.financingRound || undefined,
    financingAmount: currentProjectInput.investmentAmount || undefined,
    reportType: reportTypeOverride || document.getElementById('icm-report-type').value,
    reportVersion: document.getElementById('icm-version').value.trim() || 'v0.1-draft',
    author: document.getElementById('icm-author').value.trim() || 'Business Portal',
    bpSummary: bpSummary || undefined,
    ddSummary: ddSummary || undefined,
    contractSummary: contractSummary || undefined,
    riskNotes: riskNotes || undefined,
    meetingDate: meetingDate || undefined,
    meetingTitle: meetingTitle || undefined,
    attendees: attendees || undefined,
    discussionNotes: discussionNotes || undefined,
    resolutions: resolutions || undefined,
    actionItems: actionItems || undefined,
    dataSources: [
      bpSummary ? 'Portal.BP摘要' : '',
      ddSummary ? 'Portal.尽调摘要' : '',
      contractSummary ? 'Portal.合同摘要' : '',
      riskNotes ? 'Portal.风险备注' : '',
      discussionNotes || resolutions || actionItems ? 'Portal.会上记录' : '',
      currentProjectId ? 'Portal.当前项目' : ''
    ].filter(Boolean)
  };
}

function reviewLevelBadge(level) {
  if (level === 'must_review') return { label: '重点审核', color: '#FCA5A5' };
  if (level === 'auto_pass') return { label: '自动通过', color: '#86EFAC' };
  return { label: '需要关注', color: '#FDE68A' };
}

function renderIcMaterialsResult(result) {
  latestIcMaterials = result;
  const info = result.basicInfo || {};
  const name = info.projectName || info.companyName || '当前项目';
  document.getElementById('ic-material-title').textContent =
    `${name} — ${info.reportTypeLabel || '投决材料'}`;
  document.getElementById('icm-badge').textContent =
    `${info.reportVersion || 'draft'} · ${result.fullReport?.chapters?.length || 0} 章`;
  document.getElementById('icm-empty').style.display = 'none';
  document.getElementById('icm-report').style.display = 'block';

  const exec = result.executiveSummary || {};
  document.getElementById('icm-exec-narrative').textContent = exec.narrative || '';
  document.getElementById('icm-highlights').innerHTML = (exec.highlights || []).length
    ? exec.highlights.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无</li>';
  document.getElementById('icm-key-risks').innerHTML = (exec.keyRisks || []).length
    ? exec.keyRisks.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无</li>';

  const chapters = result.fullReport?.chapters || [];
  document.getElementById('icm-chapters').innerHTML = chapters.map((chapter) => {
    const badge = reviewLevelBadge(chapter.reviewLevel);
    return `
      <div style="border:1px solid #374151;border-radius:4px;padding:8px 10px;">
        <div style="display:flex;justify-content:space-between;gap:8px;align-items:center;">
          <span style="font-size:11px;font-weight:600;color:#F9FAFB;">${chapter.order}. ${escapeHtml(chapter.title)}</span>
          <span style="font-size:10px;padding:2px 6px;border-radius:3px;background:${badge.color};color:#0B1426;">${badge.label}</span>
        </div>
        <div style="font-size:11px;color:#D1D5DB;margin-top:4px;line-height:1.5;">${escapeHtml(chapter.summary || '')}</div>
      </div>`;
  }).join('');

  const scenarios = result.returnCalculation?.scenarios || [];
  document.getElementById('icm-returns').innerHTML = scenarios.length
    ? scenarios.map((item) =>
        `<div style="margin-bottom:6px;"><strong>${escapeHtml(item.label || item.name)}</strong><br>${escapeHtml(item.moicHint || '')}<br>${escapeHtml(item.irrHint || '')}</div>`
      ).join('')
    : '暂无情景';

  const validation = result.dataValidation || {};
  const validationItems = [
    ...(validation.missingFields || []).map((item) => `缺失：${item}`),
    ...(validation.inconsistencies || []).map((item) => `不一致：${item.field} — ${item.detail}`),
    ...(validation.errors || []).map((item) => `错误：${item}`)
  ];
  document.getElementById('icm-validation').innerHTML = validationItems.length
    ? validationItems.slice(0, 8).map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>未发现明显校验问题（仍须人工复核）</li>';

  const reminders = result.reviewReminders || [];
  document.getElementById('icm-reminders').innerHTML = reminders.length
    ? reminders.map((item) =>
        `<li>[${escapeHtml(item.levelLabel || item.level)}] ${escapeHtml(item.topic)} — ${escapeHtml(item.detail)}</li>`
      ).join('')
    : '<li>暂无</li>';
  document.getElementById('icm-disclaimer').textContent = result.disclaimer || '';

  const pack = result.materialPackage || {};
  const packItems = Array.isArray(pack.items) ? pack.items : [];
  const packReady = packItems.filter((item) => item.status === 'ready').length;
  const packStatusLabel = ({
    assembled: '已组装（可复核上会）',
    partial: '部分就绪（未齐套）',
    blocked: '门禁阻断（关键项缺口）',
    placeholder: '占位'
  })[pack.status] || pack.status || '--';
  document.getElementById('icm-pack-status').textContent =
    `${packStatusLabel} · ${packReady}/${packItems.length || 0} ready`;
  document.getElementById('icm-pack').innerHTML = packItems.length
    ? packItems.map((item) =>
        `<li>[${escapeHtml(item.status === 'ready' ? 'ready' : 'pending')}] ${escapeHtml(item.name)}${item.note ? ' — ' + escapeHtml(item.note) : ''}</li>`
      ).join('')
    : '<li>暂无材料包清单</li>';

  const gateBanner = document.getElementById('icm-gate-banner');
  if (gateBanner) {
    const meetingReady = pack.meetingReady === true;
    const gateMessage = pack.gateMessage ||
      (meetingReady
        ? '关键材料项已齐套；仍须人工复核后方可上会。'
        : '未齐套不可视为可上会：关键项仍有 pending，当前导出仅为草稿。');
    if (!meetingReady || pack.status === 'partial' || pack.status === 'blocked') {
      gateBanner.style.display = 'block';
      gateBanner.style.borderColor = pack.status === 'blocked' ? '#EF4444' : '#F59E0B';
      gateBanner.style.background = pack.status === 'blocked' ? '#7F1D1D' : '#78350F';
      gateBanner.innerHTML =
        `<strong>【${escapeHtml(pack.exportMode === 'draft' ? 'DRAFT 草稿' : '门禁提示')}】未齐套不可视为可上会</strong><br>${escapeHtml(gateMessage)}` +
        (Array.isArray(pack.criticalPending) && pack.criticalPending.length
          ? `<br>关键 pending：${escapeHtml(pack.criticalPending.join('、'))}`
          : '');
    } else {
      gateBanner.style.display = 'block';
      gateBanner.style.borderColor = '#10B981';
      gateBanner.style.background = '#064E3B';
      gateBanner.style.color = '#D1FAE5';
      gateBanner.innerHTML = `<strong>材料包关键项已齐套</strong><br>${escapeHtml(gateMessage)}`;
    }
  }

  const hist = result.historicalComparison || {};
  const histCases = Array.isArray(hist.matchedCases) ? hist.matchedCases : [];
  const decisionComparable = Array.isArray(result.decisionSummary?.comparableCases)
    ? result.decisionSummary.comparableCases
    : [];
  const displayCases = histCases.length ? histCases : decisionComparable;
  const histStatusLabel = ({
    matched: '已匹配',
    empty: '无匹配',
    placeholder: '占位'
  })[hist.status] || hist.status || '--';
  document.getElementById('icm-hist-status').textContent =
    `${histStatusLabel} · ${displayCases.length} 案`;
  const histSummaryEl = document.getElementById('icm-hist-summary');
  if (histSummaryEl) {
    histSummaryEl.textContent =
      result.decisionSummary?.comparableSummary ||
      (hist.notes && hist.notes[0]) ||
      '';
  }
  const histLines = [
    ...displayCases.map((item) =>
      `${item.caseName}${item.similarityHint ? ' · ' + item.similarityHint : ''}${item.notes ? ' — ' + item.notes : ''}`
    ),
    ...(hist.notes || []).slice(0, 3)
  ];
  document.getElementById('icm-history').innerHTML = histLines.length
    ? histLines.slice(0, 12).map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无可比案例（请确认行业字段与 Investment Memory）</li>';

  const minutes = result.meetingMinutes || {};
  const minutesStatusLabel = ({
    draft: '会后草稿',
    pre_meeting: '会前骨架',
    placeholder: '占位'
  })[minutes.status] || minutes.statusLabel || minutes.status || '--';
  const minutesStatusEl = document.getElementById('icm-minutes-status');
  if (minutesStatusEl) {
    minutesStatusEl.textContent = minutesStatusLabel;
  }
  const minutesMetaEl = document.getElementById('icm-minutes-meta');
  if (minutesMetaEl) {
    minutesMetaEl.textContent = [
      minutes.meetingTitle || '',
      minutes.meetingDate ? `日期 ${minutes.meetingDate}` : '',
      Array.isArray(minutes.attendees) && minutes.attendees.length
        ? `出席 ${minutes.attendees.join('、')}`
        : '出席待补'
    ].filter(Boolean).join(' · ');
  }
  const agendaEl = document.getElementById('icm-minutes-agenda');
  if (agendaEl) {
    const agenda = Array.isArray(minutes.agendaDraft) ? minutes.agendaDraft : [];
    agendaEl.innerHTML = agenda.length
      ? agenda.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
      : '<li>暂无</li>';
  }
  const discussEl = document.getElementById('icm-minutes-discuss');
  if (discussEl) {
    const discussLines = [
      ...(Array.isArray(minutes.keyDiscussions)
        ? minutes.keyDiscussions.map((item) => `${item.topic || '讨论'}：${item.summary || ''}`)
        : []),
      ...(Array.isArray(minutes.resolutions)
        ? minutes.resolutions.map((item) => `决议：${item}`)
        : []),
      ...(Array.isArray(minutes.actionItems)
        ? minutes.actionItems.map((item) => `待办：${item}`)
        : []),
      ...(Array.isArray(minutes.pendingResolutions) && !(minutes.resolutions || []).length
        ? minutes.pendingResolutions.slice(0, 4).map((item) => `待决议：${item}`)
        : [])
    ];
    discussEl.innerHTML = discussLines.length
      ? discussLines.slice(0, 10).map((item) => `<li>${escapeHtml(item)}</li>`).join('')
      : '<li>暂无</li>';
  }
  const minutesNotesEl = document.getElementById('icm-minutes-notes');
  if (minutesNotesEl) {
    minutesNotesEl.textContent = (minutes.notes || []).join(' ');
  }

  const exportBtn = document.getElementById('icm-export-md-btn');
  if (exportBtn) {
    exportBtn.disabled = !(result.exportMarkdown && result.exportMarkdown.content);
    exportBtn.textContent =
      pack.exportMode === 'draft' ? '导出 Markdown（草稿）' : '导出 Markdown';
  }
  const exportDocxBtn = document.getElementById('icm-export-docx-btn');
  if (exportDocxBtn) {
    exportDocxBtn.disabled = !(
      result.exportWord &&
      result.exportWord.contentBase64
    );
    exportDocxBtn.textContent =
      pack.exportMode === 'draft' ? '导出 Word（草稿）' : '导出 Word';
  }
  const exportPdfBtn = document.getElementById('icm-export-pdf-btn');
  if (exportPdfBtn) {
    exportPdfBtn.disabled = !(
      result.exportPdf &&
      result.exportPdf.contentBase64
    );
    exportPdfBtn.textContent =
      pack.exportMode === 'draft' ? '导出 PDF（草稿）' : '导出 PDF';
  }

  renderIcMaterialsOnP6(result);
  const panel = document.getElementById('stage-result-p5');
  if (panel) {
    const gateHint = pack.meetingReady
      ? '关键项已齐套（仍须人工复核）'
      : '未齐套不可视为可上会';
    panel.innerHTML = `
      <strong>投决材料已生成</strong>
      · ${escapeHtml(info.reportTypeLabel || '')}
      · taskId <code style="font-size:10px;">${escapeHtml(result.taskId || '')}</code>
      · 高风险 ${(result.riskSummary?.high || []).length}
      · 材料包 ${escapeHtml(packStatusLabel)}
      · <span style="color:${pack.meetingReady ? '#065F46' : '#9A3412'};font-weight:600;">${escapeHtml(gateHint)}</span>
      · 纪要 ${escapeHtml(minutesStatusLabel)}
      · 可比案例 ${displayCases.length}
      · 审核提示 ${reminders.length}`;
  }
}

function renderIcMaterialsOnP6(result) {
  if (!result) return;
  const summary = result.decisionSummary || {};
  document.getElementById('icm-p6-empty').style.display = 'none';
  document.getElementById('icm-p6-body').style.display = 'block';
  document.getElementById('icm-p6-badge').textContent =
    result.basicInfo?.reportTypeLabel || '已生成';
  document.getElementById('icm-p6-badge').className = 'badge badge-gold';
  document.getElementById('icm-p6-snapshot').textContent = summary.projectSnapshot || '';
  document.getElementById('icm-p6-focus').innerHTML = (summary.discussionFocus || []).length
    ? summary.discussionFocus.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无</li>';
  document.getElementById('icm-p6-open').innerHTML = (summary.openQuestions || []).length
    ? summary.openQuestions.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>暂无</li>';
  document.getElementById('icm-p6-reminders').innerHTML = (result.reviewReminders || []).length
    ? result.reviewReminders.slice(0, 6).map((item) =>
        `<li>[${escapeHtml(item.levelLabel || item.level)}] ${escapeHtml(item.topic)} — ${escapeHtml(item.detail)}</li>`
      ).join('')
    : '<li>暂无</li>';
  const pack = result.materialPackage || {};
  const packItems = Array.isArray(pack.items) ? pack.items : [];
  const packReady = packItems.filter((item) => item.status === 'ready').length;
  const hist = result.historicalComparison || {};
  const histCases = Array.isArray(hist.matchedCases) ? hist.matchedCases : [];
  const decisionComparable = Array.isArray(summary.comparableCases)
    ? summary.comparableCases
    : [];
  const displayCases = histCases.length ? histCases : decisionComparable;

  const gateEl = document.getElementById('icm-p6-gate');
  if (gateEl) {
    if (pack.meetingReady) {
      gateEl.style.display = 'none';
    } else {
      gateEl.style.display = 'block';
      gateEl.innerHTML =
        `<strong>未齐套不可视为可上会</strong><br>${escapeHtml(pack.gateMessage || '关键材料项仍有 pending，当前仅为草稿。')}` +
        (Array.isArray(pack.criticalPending) && pack.criticalPending.length
          ? `<br>关键 pending：${escapeHtml(pack.criticalPending.join('、'))}`
          : '');
    }
  }

  const histStatusEl = document.getElementById('icm-p6-hist-status');
  if (histStatusEl) {
    const histStatusLabel = ({
      matched: '已匹配',
      empty: '无匹配',
      placeholder: '占位'
    })[hist.status] || hist.status || '--';
    histStatusEl.textContent = `${histStatusLabel} · ${displayCases.length} 案`;
  }
  const histSummaryEl = document.getElementById('icm-p6-hist-summary');
  if (histSummaryEl) {
    histSummaryEl.textContent =
      summary.comparableSummary ||
      (hist.notes && hist.notes[0]) ||
      '暂无可比案例写入决策摘要。';
  }
  const histListEl = document.getElementById('icm-p6-history');
  if (histListEl) {
    histListEl.innerHTML = displayCases.length
      ? displayCases.slice(0, 8).map((item) =>
          `<li><strong>${escapeHtml(item.caseName || '')}</strong>${item.similarityHint ? ' · ' + escapeHtml(item.similarityHint) : ''}${item.notes ? '<br><span style="color:#6B7280;">' + escapeHtml(item.notes) + '</span>' : ''}</li>`
        ).join('')
      : '<li>暂无可比案例（请确认行业与 Memory）</li>';
  }

  const packEl = document.getElementById('icm-p6-pack');
  if (packEl) {
    packEl.textContent =
      `材料包 ${pack.status || '--'}（${packReady}/${packItems.length || 0} ready，${pack.exportMode || 'draft'}）· 可比案例 ${displayCases.length}` +
      (pack.meetingReady ? ' · 关键项齐套' : ' · 未齐套不可视为可上会');
  }
  document.getElementById('icm-p6-disclaimer').textContent = result.disclaimer || '';
}

async function generateIcMaterialsReport(button, options = {}) {
  const payload = buildIcMaterialsPayload(options.reportType);
  if (
    !payload.projectName &&
    !payload.bpSummary &&
    !payload.ddSummary &&
    !payload.contractSummary &&
    !payload.riskNotes
  ) {
    showRuntimeError('请先立项/打开项目，或填写 BP/尽调/合同/风险摘要。');
    return;
  }
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '生成中...';
  setRuntimeProgress(30);
  try {
    const result = await requestRuntime('/api/business/ic-materials/generate', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    renderIcMaterialsResult(result);
    setRuntimeProgress(100);
    if (options.gotoPage) {
      document.querySelector(`[data-page="${options.gotoPage}"]`)?.click();
    }
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
    const empty = document.getElementById('icm-empty');
    if (empty) {
      empty.style.display = 'flex';
      empty.textContent = `生成失败：${error.message}`;
    }
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
}

document.getElementById('icm-from-project').addEventListener('click', function() {
  if (!currentProjectId && !currentProjectInput.companyName) {
    showRuntimeError('请先立项或打开项目。');
    return;
  }
  const name =
    currentProjectInput.companyName ||
    document.getElementById('ic-project-name')?.textContent?.trim() ||
    currentProjectId;
  document.getElementById('ic-project-name').innerHTML = `<strong>${escapeHtml(name)}</strong>`;
  if (!document.getElementById('icm-bp').value.trim()) {
    const parts = [
      currentProjectInput.companyName ? `公司：${currentProjectInput.companyName}` : '',
      currentProjectInput.industryName || currentProjectInput.industry
        ? `行业：${currentProjectInput.industryName || currentProjectInput.industry}`
        : '',
      currentProjectInput.financingRound
        ? `轮次：${currentProjectInput.financingRound}`
        : '',
      currentProjectInput.investmentAmount
        ? `金额：${currentProjectInput.investmentAmount}`
        : '',
      currentProjectInput.documentContent
        ? String(currentProjectInput.documentContent).slice(0, 4000)
        : ''
    ].filter(Boolean);
    document.getElementById('icm-bp').value = parts.join('\n');
  }
});

document.getElementById('icm-generate-btn').addEventListener('click', async function() {
  await generateIcMaterialsReport(this, { gotoPage: 'p5' });
});

document.getElementById('icm-export-md-btn')?.addEventListener('click', function() {
  const bundle = latestIcMaterials?.exportMarkdown;
  if (!bundle || !bundle.content) {
    showRuntimeError('请先生成投决材料后再导出。');
    return;
  }
  const blob = new Blob([bundle.content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = bundle.fileName || 'ic-materials.md';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
});

document.getElementById('icm-export-docx-btn')?.addEventListener('click', function() {
  const bundle = latestIcMaterials?.exportWord;
  if (!bundle || !bundle.contentBase64) {
    showRuntimeError('请先生成投决材料后再导出 Word。');
    return;
  }
  const binary = atob(bundle.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: bundle.mimeType ||
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = bundle.fileName || 'ic-materials.docx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
});

document.getElementById('icm-export-pdf-btn')?.addEventListener('click', function() {
  const bundle = latestIcMaterials?.exportPdf;
  if (!bundle || !bundle.contentBase64) {
    showRuntimeError('请先生成投决材料后再导出 PDF。');
    return;
  }
  const pack = latestIcMaterials?.materialPackage || {};
  if (pack.meetingReady !== true) {
    // 未齐套仍允许导出草稿，但明确提示门禁
    console.info('[IC] 导出 PDF 草稿：未齐套不可视为可上会', pack.gateMessage || '');
  }
  const binary = atob(bundle.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: bundle.mimeType || 'application/pdf'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = bundle.fileName || 'ic-materials.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
});

document.getElementById('icm-p6-generate-btn').addEventListener('click', async function() {
  await generateIcMaterialsReport(this, {
    reportType: 'summary_report',
    gotoPage: 'p6'
  });
});

function renderExitWorkflowResult(result) {
  const name = result.company?.companyName || currentProjectInput.companyName || result.projectId || '当前项目';
  const decision = result.decision || {};
  const exitSkill = (result.skillResults || []).find((item) => item.skillName === 'ExitSkill');
  const output = exitSkill?.output || {};
  document.getElementById('exit-project-name').textContent = name;
  document.getElementById('exit-decision-badge').textContent = decisionLabel(decision.result);
  document.getElementById('exit-project-summary').textContent =
    output.summary || formatDecision(decision) || '退出 Workflow 已完成。';
  document.getElementById('exit-book-moic').textContent = output.bookMoic || '--';
  document.getElementById('exit-irr').textContent = output.irrPct || '--';
  document.getElementById('exit-timing').textContent = output.timingAdvice || '暂无时机建议';
  const paths = Array.isArray(output.pathOptions) ? output.pathOptions : [];
  document.getElementById('exit-path-list').innerHTML = paths.length
    ? paths.map((item, index) => `
        <div style="background:${index === 0 || item.path === output.recommendedPath ? '#F0FDF4' : '#F9FAFB'};border:1px solid #E5E7EB;border-radius:5px;padding:12px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-size:13px;font-weight:600;">${escapeHtml(item.path || '路径')}</span>
            <span style="font-size:11px;color:#16A34A;font-weight:600;">可行性 ${escapeHtml(String(item.feasibility ?? '--'))}%</span>
          </div>
          <p style="font-size:11px;color:#374151;line-height:1.6;margin:0;">
            回报 ${escapeHtml(item.moicRange || '--')} · 周期 ${escapeHtml(item.timeline || '--')}<br>
            ${escapeHtml(item.notes || '')}
          </p>
        </div>
      `).join('')
    : '<div style="background:#F9FAFB;border:1px solid #E5E7EB;border-radius:5px;padding:12px;font-size:11px;color:#6B7280;">未返回路径对比</div>';
  const evidence = (result.evidence || []).filter((item) => item.source === 'exit_analysis');
  document.getElementById('exit-evidence').innerHTML = evidence.length
    ? evidence.map((item) => `<li>${escapeHtml(item.description)}</li>`).join('')
    : '<li>暂无退出证据</li>';
}

function renderStageResult(stagePage, result) {
  const panel = document.getElementById(`stage-result-${stagePage}`);
  if (!panel) return;
  const artifactLinks = (result.artifacts || [])
    .map(artifact => `<a href="${runtimeBaseUrl}${artifact.uri}" target="_blank" rel="noopener" style="color:#C5A55A;">${escapeHtml(artifact.title)}</a>`)
    .join(' · ') || '暂无报告';
  const approvalBlock = isAwaitingApproval(result)
    ? buildApprovalBannerHtml(result.runId)
    : '';
  panel.innerHTML = `
    ${approvalBlock}
    <strong>${escapeHtml(stageLabel(result.stage || result.context?.projectInfo?.stage))}</strong>
    · 决策 ${escapeHtml(decisionLabel(result.decision?.result))}
    · 评分 ${result.evaluation?.score ?? '--'}
    · runId <code style="font-size:10px;">${escapeHtml(result.runId || '')}</code>
    <div style="margin-top:6px;">${escapeHtml(formatDecision(result.decision))}</div>
    <div style="margin-top:6px;">报告：${artifactLinks}</div>`;
  if (typeof applyRbacUiGates === 'function') applyRbacUiGates();
}

function updateStageProjectLabels(result) {
  const name = result.company?.companyName || currentProjectInput.companyName || result.projectId || '当前项目';
  const icName = document.getElementById('ic-project-name');
  if (icName) icName.innerHTML = `<strong>${escapeHtml(name)}</strong>`;
  const icTitle = document.getElementById('ic-material-title');
  if (icTitle) icTitle.textContent = `${name} — 投委会材料`;
  const decisionProject = document.getElementById('ic-decision-project');
  if (decisionProject) decisionProject.innerHTML = `<strong>${escapeHtml(name)}</strong>`;
  const exitName = document.getElementById('exit-project-name');
  if (exitName && exitName.textContent === '未选择项目') {
    exitName.textContent = name;
  }
}

let prejudgeLoadingTimer = null;

function startPreJudgmentLoadingAnimation() {
  const initialState = document.getElementById('prejudge-initial-state');
  const loadingState = document.getElementById('prejudge-loading-state');
  if (initialState) initialState.style.display = 'none';
  if (loadingState) loadingState.style.display = 'flex';

  const scoreEl = document.getElementById('prejudge-loading-score');
  const countEl = document.getElementById('prejudge-loading-count');
  const barEl = document.getElementById('prejudge-loading-bar-fill');
  let step = 0;
  const totalSteps = 50;
  if (prejudgeLoadingTimer) {
    clearInterval(prejudgeLoadingTimer);
  }
  prejudgeLoadingTimer = setInterval(() => {
    step = Math.min(step + Math.floor(Math.random() * 3) + 1, totalSteps);
    const score = Math.min(50 + Math.floor(step * 0.8) + Math.floor(Math.random() * 6), 92);
    if (scoreEl) scoreEl.textContent = String(score);
    if (countEl) countEl.textContent = `已扫描 ${step} / ${totalSteps} 项指标`;
    if (barEl) barEl.style.width = `${(step / totalSteps) * 100}%`;
    if (step >= totalSteps) {
      clearInterval(prejudgeLoadingTimer);
      prejudgeLoadingTimer = null;
    }
  }, 2200);
}

function stopPreJudgmentLoadingAnimation() {
  if (prejudgeLoadingTimer) {
    clearInterval(prejudgeLoadingTimer);
    prejudgeLoadingTimer = null;
  }
  const loadingState = document.getElementById('prejudge-loading-state');
  if (loadingState) loadingState.style.display = 'none';
  const initialState = document.getElementById('prejudge-initial-state');
  if (initialState) initialState.style.display = 'block';
}

function showPrejudgeCheckMessage(message, type = 'error') {
  const el = document.getElementById('prejudge-check-message');
  if (!el) return;
  if (!message) {
    el.style.display = 'none';
    el.textContent = '';
    el.className = 'prejudge-check-message';
    return;
  }
  el.textContent = message;
  el.className = `prejudge-check-message ${type === 'info' ? 'info' : ''}`;
  el.style.display = 'block';
}

/** 记录已完成 AI 证据匹配的 archive，避免重复自动触发 */
let evidenceMatchLoadedFor = '';

async function loadPreJudgmentCheck() {
  if (isLoadingPrejudgmentCheck) return;
  isLoadingPrejudgmentCheck = true;
  const dashboard = document.getElementById('prejudge-dashboard');
  const emptySection = document.getElementById('prejudge-empty');
  if (dashboard) dashboard.classList.remove('active');
  if (emptySection) emptySection.style.display = 'flex';
  stopPreJudgmentLoadingAnimation();
  const initialState = document.getElementById('prejudge-initial-state');
  if (initialState) initialState.style.display = 'block';
  document.getElementById('prejudge-status').textContent = 'DATA CHECK · 数据检查';
  showPrejudgeCheckMessage('');
  if (!latestBpParse?.archive) {
    showPrejudgeCheckMessage('请先完成 BP 建档解析（上传 BP 并解析），才能进入前置研判。', 'error');
    isLoadingPrejudgmentCheck = false;
    return;
  }
  try {
    const dimsContainer = document.getElementById('prejudge-check-dims');
    if (dimsContainer) dimsContainer.innerHTML = '<div style="padding:12px;color:#6B7280;font-size:11px;">正在加载评分卡...</div>';
    if (!latestScorecard) {
      latestScorecard = await requestRuntime('/api/business/pre-judgment/scorecard', { method: 'GET', timeoutMs: 15000 });
    }
    renderPrejudgmentCheck(latestScorecard, latestBpParse.archive);
    // 首次进入且尚无证据时，自动按打分卡模板执行 AI 证据匹配（预填证据链）
    const taskKey = latestBpParse.archive.taskId || latestBpParse.archive.bpFileName || 'archive';
    const hasAnyEvidence = Object.keys(latestBpParse.archive.fields || {})
      .some((key) => key.endsWith('_evidence'));
    if (!hasAnyEvidence && !latestPreJudgment && evidenceMatchLoadedFor !== taskKey) {
      void matchPreJudgmentEvidence(document.getElementById('prejudge-match-evidence'));
    }
  } catch (error) {
    showPrejudgeCheckMessage(`加载评分卡失败：${error.message}`, 'error');
  } finally {
    isLoadingPrejudgmentCheck = false;
  }
}

/**
 * AI 证据匹配：按打分卡模板逐指标从 BP/建档材料中查找评分依据并预填到检查表。
 * 不覆盖手动补充的证据；匹配完成后旧评分结果作废。
 */
async function matchPreJudgmentEvidence(button) {
  if (!latestBpParse?.archive) {
    showPrejudgeCheckMessage('请先完成 BP 建档解析。', 'error');
    return;
  }
  const taskKey = latestBpParse.archive.taskId || latestBpParse.archive.bpFileName || 'archive';
  const originalText = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = 'AI 匹配中...';
  }
  showPrejudgeCheckMessage('正在按打分卡模板逐项匹配 50 项指标证据（约 1 分钟，仅引用 BP/建档事实，未披露项保持空白）...', 'info');
  try {
    const result = await requestRuntime('/api/business/pre-judgment/match-evidence', {
      method: 'POST',
      body: JSON.stringify({ archive: latestBpParse.archive }),
      timeoutMs: 300000,
    });
    const fields = latestBpParse.archive.fields || (latestBpParse.archive.fields = {});
    let filled = 0;
    for (const item of result.matches || []) {
      if (!item.found || !item.evidence) continue;
      const key = `${item.criterionId}_evidence`;
      const existing = fields[key];
      if (existing?.source === 'manual' && String(existing?.value || '').trim()) continue;
      fields[key] = {
        value: String(item.evidence).trim(),
        source: 'ai_match',
        confidence: Number.isFinite(item.confidence) ? item.confidence : 0.6,
        needConfirm: true,
      };
      filled += 1;
    }
    evidenceMatchLoadedFor = taskKey;
    // 证据变化后旧评分作废
    if (latestPreJudgment) {
      latestPreJudgment = null;
      const dashboard = document.getElementById('prejudge-dashboard');
      if (dashboard) dashboard.classList.remove('active');
    }
    showPrejudgeCheckMessage(
      `AI 证据匹配完成：${filled} / ${result.criterionCount ?? 50} 项指标找到依据（标注「AI 匹配·待确认」）；未披露的指标保持空白，可手动补充或走外部数据源。确认后点击「开始评分」统一汇总。`,
      'info'
    );
    renderPrejudgmentCheck(latestScorecard, latestBpParse.archive);
  } catch (error) {
    showPrejudgeCheckMessage(`AI 证据匹配失败：${error.message}`, 'error');
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

function renderPrejudgmentCheck(scorecard, archive) {
  const dims = scorecard?.dimensions || [];
  const criteria = scorecard?.criteria || [];
  const criterionById = new Map(criteria.map((c) => [c.id, c]));
  const fields = archive?.fields || {};
  const container = document.getElementById('prejudge-check-dims');
  if (!container) return;

  // 模型评分结果（如已评分）：证据列必须与评分实际依据一致
  const scoredByCriterion = new Map();
  if (latestPreJudgment?.dimensions?.length) {
    for (const dim of latestPreJudgment.dimensions) {
      for (const crit of dim.criteria || []) {
        scoredByCriterion.set(crit.criterionId, crit);
      }
    }
  }

  let totalReady = 0;
  let totalScored = 0;
  let total = 0;

  const rows = dims.flatMap((dim) => {
    const dimCriteria = dim.criteriaIds
      .map((id) => criterionById.get(id))
      .filter(Boolean);
    return dimCriteria.map((c) => {
      total += 1;
      const evidenceField = fields[`${c.id}_evidence`];
      const filledEvidence = String(evidenceField?.value || '').trim();
      const isAiMatch = evidenceField?.source === 'ai_match';
      const scored = scoredByCriterion.get(c.id);
      const isScored = scored?.status === 'ai_scored' && Number.isFinite(scored?.score);
      if (isScored) totalScored += 1;
      // 证据口径：已评分 → 展示模型评分依据；未评分 → 展示 AI 匹配/手动补充；都没有 → 空着待补充
      const modelEvidence = isScored ? String(scored.evidence || '').trim() : '';
      const evidenceValue = modelEvidence || filledEvidence;
      const hasEvidence = evidenceValue.length > 0;
      if (hasEvidence) totalReady += 1;
      const readyClass = hasEvidence ? 'ready' : 'missing';
      const evidenceDisplay = hasEvidence
        ? (evidenceValue.length > 120 ? evidenceValue.slice(0, 120) + '…' : evidenceValue)
        : '暂无证据';
      const evidenceTag = modelEvidence
        ? ' <span style="color:#9CA3AF;font-size:10px;">（模型评分依据）</span>'
        : filledEvidence
          ? (isAiMatch
            ? ' <span style="color:#B45309;font-size:10px;">（AI 匹配·待确认）</span>'
            : ' <span style="color:#9CA3AF;font-size:10px;">（手动补充）</span>')
          : '';
      const statusBadge = isScored
        ? `<span class="status-badge ready">已评 ${scored.score} 分</span>`
        : hasEvidence
          ? (isAiMatch
            ? '<span class="status-badge ready">AI 匹配</span>'
            : '<span class="status-badge ready">手动补充</span>')
          : '<span class="status-badge missing">待补充</span>';
      return `
        <tr class="${readyClass}" data-criterion-id="${escapeHtml(c.id)}">
          <td class="dim-cell">${escapeHtml(dim.name)}</td>
          <td class="category-cell">${escapeHtml(c.category)}</td>
          <td class="name-cell">${escapeHtml(c.name)}</td>
          <td class="evidence-cell ${hasEvidence ? '' : 'empty'}">${escapeHtml(evidenceDisplay)}${evidenceTag}</td>
          <td>${statusBadge}</td>
          <td>
            ${hasEvidence
              ? `<button class="add-evidence-btn" type="button" data-edit-criterion="${escapeHtml(c.id)}">编辑</button>`
              : `<button class="add-evidence-btn" type="button" data-add-criterion="${escapeHtml(c.id)}">补充</button>`
            }
          </td>
        </tr>
      `;
    });
  });

  container.innerHTML = `
    <table class="prejudge-check-table">
      <thead>
        <tr>
          <th>维度</th>
          <th>分类</th>
          <th>指标</th>
          <th>当前证据（= 评分依据）</th>
          <th>状态</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        ${rows.join('')}
      </tbody>
    </table>
  `;

  document.getElementById('prejudge-check-subtitle').textContent = totalScored > 0
    ? `有证据 ${totalReady} / ${total} 项 · 模型已评 ${totalScored} / ${total} 项`
    : `有证据 ${totalReady} / ${total} 项 · 尚未评分`;

  // 绑定补充/编辑按钮事件
  container.querySelectorAll('button[data-add-criterion], button[data-edit-criterion]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const criterionId = btn.getAttribute('data-add-criterion') || btn.getAttribute('data-edit-criterion');
      const c = criterionById.get(criterionId);
      if (!c) return;
      const currentValue = fields[`${criterionId}_evidence`]?.value || '';
      const newValue = window.prompt(`补充指标「${c.name}」的证据：`, currentValue);
      if (newValue === null) return;
      if (!latestBpParse.archive.fields) latestBpParse.archive.fields = {};
      latestBpParse.archive.fields[`${criterionId}_evidence`] = {
        value: newValue.trim(),
        source: 'manual',
        confidence: 1,
        needConfirm: false,
      };
      // 证据变化后旧评分失效，清空结果避免展示过期分数与过期证据
      if (latestPreJudgment) {
        latestPreJudgment = null;
        const dashboard = document.getElementById('prejudge-dashboard');
        if (dashboard) dashboard.classList.remove('active');
        showPrejudgeCheckMessage('证据已更新，原评分结果已失效，请重新点击「开始评分」。', 'info');
      }
      renderPrejudgmentCheck(scorecard, latestBpParse.archive);
    });
  });
}

async function runPreJudgment(button) {
  if (!latestBpParse?.archive) {
    showRuntimeError('请先完成 BP 建档解析。');
    return;
  }
  const originalText = button?.textContent || '开始评分';
  if (button) {
    button.disabled = true;
    button.textContent = '证据研判中...';
  }
  document.getElementById('prejudge-dashboard').classList.remove('active');
  document.getElementById('prejudge-empty').style.display = 'flex';
  document.getElementById('prejudge-status').textContent = 'MODEL SCANNING · 正在匹配 50 项指标';
  startPreJudgmentLoadingAnimation();
  try {
    // 评分依据 = BP 原文 + 建档字段 + 手动补充的 _evidence（后端提取为 manualEvidence）
    const result = await requestRuntime('/api/business/pre-judgment/score', {
      method: 'POST',
      body: JSON.stringify({ archive: latestBpParse.archive })
    });
    latestPreJudgment = result;
    renderPreJudgment(result);
  } catch (error) {
    stopPreJudgmentLoadingAnimation();
    document.getElementById('prejudge-status').textContent = 'SCORING ERROR';
    const initialState = document.getElementById('prejudge-initial-state');
    if (initialState) initialState.style.display = 'block';
    const title = document.getElementById('prejudge-empty-title');
    const desc = document.getElementById('prejudge-empty-desc');
    if (title) title.textContent = '研判生成失败';
    if (desc) desc.textContent = error.message;
    showPrejudgeCheckMessage(`前置研判失败：${error.message}。可点击「外部数据源补充」或「手动编辑 BP 草稿」补充证据后重试。`, 'error');
    showRuntimeError(`前置研判失败：${error.message}`);
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

function buildProjectInputFromBpArchive(archive) {
  const fields = archive?.fields || {};
  const get = (key) => {
    const value = fields[key]?.value;
    return value === undefined || value === null ? '' : String(value).trim();
  };

  const coreSummary = get('mainBusiness');
  const appendixLines = [
    ['项目名称', get('projectName')],
    ['企业简称', get('companyShortName')],
    ['细分赛道', get('subSector')],
    ['所在地区', get('region')],
    ['成立时间', get('foundedAt')],
    ['投前估值', get('preMoneyValuation')],
    ['投后估值', get('postMoneyValuation')],
    ['募资用途', get('useOfProceeds')],
    ['核心团队', get('coreTeam')],
    ['商业模式', get('businessModel')],
    ['核心产品', get('coreProducts')],
    ['竞争优势', get('competitiveAdvantage')],
    ['财务摘要', get('financialSummary')],
    ['主要客户', get('keyCustomers')],
    ['目标客户', get('targetCustomers')],
    ['股东结构', get('shareholderStructure')],
    ['历史融资', get('fundingHistory')],
    ['注册资本', get('registeredCapital')],
    ['法定代表人', get('legalRepresentative')],
    ['信用代码', get('unifiedSocialCreditCode')],
    ['联系人', get('contactPerson')],
    ['联系方式', get('contactInfo')],
    ['出让股权', get('equityOffered')]
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}：${value}`);
  const businessSummary = appendixLines.length
    ? `${coreSummary}${coreSummary ? '\n\n' : ''}【建档补充】\n${appendixLines.join('\n')}`
    : coreSummary;

  return {
    companyName: get('companyName'),
    industryName: get('industry'),
    financingRound: get('financingRound'),
    investmentAmount: get('financingAmount'),
    revenue: get('revenue'),
    grossMargin: get('grossMargin'),
    founders: get('founders'),
    businessSummary,
    owner: 'Business Portal'
  };
}

async function fillBpFieldsFromExternalData(button) {
  if (!latestBpParse?.archive?.fields) {
    showRuntimeError('请先完成 BP 解析。');
    return;
  }
  const companyName = latestBpParse.archive.fields.companyName?.value?.trim() || latestBpParse.archive.fields.projectName?.value?.trim();
  if (!companyName) {
    showRuntimeError('缺少企业名称，无法查询外部工商数据。请在「企业名称」字段中补全。');
    return;
  }
  if (!latestXiniuStatus) {
    await refreshXiniuStatus();
  }
  if (latestXiniuStatus && latestXiniuStatus.configured === false) {
    showRuntimeError('外部工商数据未配置：请在本地 .env 设置 XINIU_API_KEY 和/或 QCC_MCP_AUTHORIZATION（或 QCC_API_TOKEN），然后重启 Runtime。');
    return;
  }
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '正在查询外部工商数据...';
  try {
    const qs = new URLSearchParams({ companyName, aspect: '企业基本信息', provider: 'auto' });
    const result = await requestRuntime(`/api/business/external-data/company?${qs.toString()}`, { timeoutMs: 90000 });
    const profile = result?.profile || (result?.provider === 'auto' && result?.results?.[0]?.profile) || {};
    if (!Object.keys(profile).length) {
      showRuntimeError('外部数据源未返回可补齐字段，可能是未配置或企业未命中。');
      return;
    }
    let filledCount = 0;
    for (const [rawKey, value] of Object.entries(profile)) {
      const key = rawKey.trim();
      const bpKey = BP_EXTERNAL_FIELD_MAP[key];
      if (!bpKey || !value) continue;
      const existing = latestBpParse.archive.fields[bpKey];
      if (!existing || !existing.value || existing.needConfirm) {
        latestBpParse.archive.fields[bpKey] = {
          value: String(value),
          source: `外部工商 · ${result.answeredBy === 'both' ? '烯牛+企查查' : result.answeredBy === 'qichacha' ? '企查查' : '烯牛'}`,
          confidence: 0.95,
          needConfirm: false
        };
        filledCount++;
      }
    }
    renderBpParseResult(latestBpParse);
    showRuntimeError(`外部数据已补齐 ${filledCount} 个字段，请确认后立项。`);
  } catch (error) {
    showRuntimeError(`外部数据补齐失败：${error.message}`);
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
}

async function createProjectFromParsedBp(button) {
  if (!latestBpParse?.intake && !latestBpParse?.archive?.fields) {
    showRuntimeError('请先完成 BP 建档解析。');
    return;
  }
  const input = buildProjectInputFromBpArchive(latestBpParse.archive);
  input.documentContent = latestBpParse.documentContent || '';
  input.documentName = latestBpParse.fileName || 'BP 商业计划书';
  input.bpFileName = latestBpParse.fileName || '';
  input.bpStoredPath = latestBpParse.storedPath || '';
  // 携带前置研判维度摘要，供初评报告四维分落库
  if (latestPreJudgment?.dimensions?.length) {
    input.preJudgment = {
      runId: latestPreJudgment.runId || '',
      dimensions: latestPreJudgment.dimensions.map((dimension) => ({
        dimensionId: dimension.dimensionId,
        name: dimension.name,
        score: dimension.score,
        maxScore: dimension.maxScore,
        assessedCount: dimension.assessedCount
      }))
    };
  }

  if (!input.companyName || !input.industryName) {
    showRuntimeError('解析结果缺少企业名称或行业，请回填后手工补全。');
    return;
  }

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '正在进入初评...';
  currentProjectInput = input;
  setRuntimeProgress(40);
  try {
    const result = await requestRuntime('/api/business/projects', {
      method: 'POST',
      body: JSON.stringify(input)
    });
    currentProjectId = result.projectId;
    localStorage.setItem('pe-ai-current-project', currentProjectId);
    setRuntimeProgress(100);
    renderRuntimeResult(result);
    renderReviewPage(result, input);
    await loadProjectList();
    document.querySelector('[data-page="p2"]').click();
  } catch (error) {
    showRuntimeError(error.message);
    setRuntimeProgress(0);
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
}

function renderPreJudgment(result) {
  stopPreJudgmentLoadingAnimation();
  document.getElementById('prejudge-empty').style.display = 'none';
  document.getElementById('prejudge-dashboard').classList.add('active');
  const levelLabels = {
    insufficient: 'EVIDENCE INSUFFICIENT · 证据不足',
    observe: 'OBSERVE · 重点观察',
    promising: 'PROMISING · 高潜信号',
    strong: 'STRONG SIGNAL · 强信号'
  };
  document.getElementById('prejudge-status').textContent =
    levelLabels[result.signalLevel] || 'DRAFT READY';
  document.querySelector('.score-orbit').style.setProperty(
    '--score-angle',
    `${Math.max(0, Math.min(360, (result.totalScore || 0) * 3.6))}deg`
  );
  animateScoreNumber('prejudge-total-score', result.totalScore || 0);
  document.getElementById('prejudge-coverage').textContent =
    `${Math.round((result.coverage || 0) * 100)}%`;
  document.getElementById('prejudge-confidence').textContent =
    `${Math.round((result.confidence || 0) * 100)}%`;
  document.getElementById('prejudge-assessed').textContent =
    `${result.assessedCount || 0}/${result.criterionCount || 0}`;
  document.getElementById('prejudge-deduction').textContent =
    `${result.deductionTotal || 0}`;
  document.getElementById('prejudge-disclaimer').textContent =
    result.disclaimer || '无证据指标不按 0 分处理，所有结果须人工确认。';

  const dimensions = result.dimensions || [];
  document.getElementById('prejudge-dimensions').innerHTML = dimensions.map((dimension) => {
    const scorePercent = dimension.maxScore
      ? Math.max(0, Math.min(100, dimension.score / dimension.maxScore * 100))
      : 0;
    const coverage = dimension.coverage || 0;
    const statusClass = coverage >= 0.5 ? 'complete' : coverage > 0 ? 'partial' : 'empty';
    return `<div class="dimension-card ${statusClass}">
      <div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;">
        <span class="dim-name">${escapeHtml(dimension.name)}</span>
        <strong class="dim-score">${dimension.score}/${dimension.maxScore}</strong>
      </div>
      <div class="dim-coverage">覆盖 ${Math.round(coverage * 100)}% · ${dimension.assessedCount}/${dimension.criterionCount} 指标</div>
      <div class="dimension-line"><div class="dimension-fill" style="width:${scorePercent}%"></div></div>
    </div>`;
  }).join('');
  renderPreJudgmentRadar(dimensions);

  const evaluated = dimensions
    .flatMap((dimension) => dimension.criteria.map((criterion) => ({ ...criterion, dimensionName: dimension.name })))
    .sort((left, right) => {
      if ((left.score || 0) < 0 && (right.score || 0) >= 0) return -1;
      if ((right.score || 0) < 0 && (left.score || 0) >= 0) return 1;
      return (right.confidence || 0) - (left.confidence || 0);
    });
  const scored = evaluated.filter((item) => item.status === 'ai_scored');
  const unknown = evaluated.filter((item) => item.status === 'unknown');
  document.getElementById('prejudge-evidence-count').textContent =
    `已评分 ${scored.length} · 待核验 ${unknown.length}`;
  const signalHtml = scored.slice(0, 24).map((item) => {
    const className = (item.score || 0) < 0 ? 'signal-item risk' : 'signal-item';
    const scoreText = (item.score || 0) > 0 ? `+${item.score}` : `${item.score}`;
    return `<div class="${className}">
      <div style="display:flex;justify-content:space-between;gap:6px;"><strong>${escapeHtml(item.dimensionName)} / ${escapeHtml(item.name)}</strong><span>${scoreText}</span></div>
      <div style="color:#86A9BA;margin-top:3px;">${escapeHtml(item.evidence)}</div>
      <div style="color:#567487;margin-top:3px;">置信度 ${Math.round(item.confidence * 100)}%${item.needConfirm ? ' · 待确认' : ''}</div>
    </div>`;
  }).join('');
  const unknownHtml = unknown.length
    ? `<div class="signal-item unknown">${unknown.length} 项指标因 BP 缺少证据保持 unknown，不计 0 分。</div>`
    : '';
  document.getElementById('prejudge-signals').innerHTML =
    (signalHtml || '<div class="signal-item unknown">BP 未提供足够评分证据。</div>') +
    unknownHtml;
}

function renderPreJudgmentRadar(dimensions) {
  const centerX = 210;
  const centerY = 150;
  const radius = 105;
  const count = Math.max(6, dimensions.length);
  const pointAt = (index, value, extraRadius = radius) => {
    const angle = -Math.PI / 2 + index * Math.PI * 2 / count;
    const r = extraRadius * value;
    return [centerX + Math.cos(angle) * r, centerY + Math.sin(angle) * r];
  };
  const grid = [];
  for (let level = 1; level <= 5; level += 1) {
    grid.push(`<polygon class="radar-grid" points="${Array.from({length: count}, (_, index) => pointAt(index, level / 5).join(',')).join(' ')}"></polygon>`);
  }
  document.getElementById('radar-grid').innerHTML = grid.join('');
  document.getElementById('radar-axes').innerHTML = Array.from({length: count}, (_, index) => {
    const [x, y] = pointAt(index, 1);
    return `<line class="radar-axis" x1="${centerX}" y1="${centerY}" x2="${x}" y2="${y}"></line>`;
  }).join('');
  const values = Array.from({length: count}, (_, index) => {
    const dimension = dimensions[index];
    return dimension?.maxScore ? Math.max(0, Math.min(1, dimension.score / dimension.maxScore)) : 0;
  });
  const points = values.map((value, index) => pointAt(index, value));
  document.getElementById('radar-area').setAttribute('points', points.map((point) => point.join(',')).join(' '));
  document.getElementById('radar-nodes').innerHTML = points.map(([x, y]) =>
    `<circle class="radar-node" cx="${x}" cy="${y}" r="4"></circle>`
  ).join('');
  document.getElementById('radar-labels').innerHTML = Array.from({length: count}, (_, index) => {
    const [x, y] = pointAt(index, 1, 132);
    const dimension = dimensions[index];
    return `<text class="radar-label" x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle">${escapeHtml(dimension?.name || '')}</text>`;
  }).join('');
}

function animateScoreNumber(elementId, target) {
  const element = document.getElementById(elementId);
  const started = performance.now();
  const duration = 900;
  const animate = (now) => {
    const progress = Math.min(1, (now - started) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.textContent = Math.round(target * eased);
    if (progress < 1) requestAnimationFrame(animate);
  };
  requestAnimationFrame(animate);
}

function createNeuralParticles() {
  const shell = document.getElementById('prejudge-shell');
  if (shell.querySelector('.neural-particle')) return;
  for (let index = 0; index < 22; index += 1) {
    const node = document.createElement('span');
    node.className = 'neural-particle';
    node.style.left = `${5 + Math.random() * 90}%`;
    node.style.top = `${5 + Math.random() * 90}%`;
    node.style.setProperty('--speed', `${5 + Math.random() * 8}s`);
    node.style.setProperty('--dx', `${-40 + Math.random() * 80}px`);
    node.style.setProperty('--dy', `${-70 + Math.random() * 100}px`);
    shell.appendChild(node);
  }
}

function renderBpParseResult(result) {
  const archive = result.archive || {};
  const fields = archive.fields || {};
  const generated = archive.generatedContent || {};
  const validation = archive.validation || {};
  const fieldLabels = {
    projectName: '项目名称', companyName: '企业名称', companyShortName: '企业简称',
    industry: '所属行业', subSector: '细分赛道', region: '所在地区', foundedAt: '成立时间',
    founders: '创始人', coreTeam: '核心团队', financingRound: '融资轮次',
    financingAmount: '融资金额', preMoneyValuation: '投前估值', postMoneyValuation: '投后估值',
    useOfProceeds: '募资用途', mainBusiness: '主营业务', businessModel: '商业模式',
    coreProducts: '核心产品', financialSummary: '财务摘要', revenue: '收入',
    grossMargin: '毛利率', profit: '利润', cashFlow: '现金流',
    competitiveAdvantage: '竞争优势', keyCustomers: '主要客户', targetCustomers: '目标客户',
    shareholderStructure: '股东结构', fundingHistory: '历史融资', registeredCapital: '注册资本',
    legalRepresentative: '法定代表人', unifiedSocialCreditCode: '信用代码',
    contactPerson: '联系人', contactInfo: '联系方式', equityOffered: '出让股权'
  };

  document.getElementById('bp-result-file').textContent =
    `${result.fileName || archive.bpFileName || '已解析'} · ${archive.parseStatus || 'success'}`;
  document.getElementById('bp-result-disclaimer').textContent =
    result.disclaimer || archive.disclaimer || '本结果为 AI 建档草稿，须人工确认。';

  const missing = validation.missingRequiredFields || [];
  const needConfirm = validation.needConfirmFields || [];
  document.getElementById('bp-result-validation').innerHTML = [
    `<span class="badge badge-gold">字段 ${Object.keys(fields).length}</span>`,
    missing.length ? `<span class="badge badge-orange">缺失必填 ${missing.length}</span>` : '',
    needConfirm.length ? `<span class="badge badge-orange">待确认 ${needConfirm.length}</span>` : '<span class="badge badge-green">无强制待确认</span>'
  ].join('');

  // 渲染可编辑字段：每个字段都是 input，支持手动修改
  const orderedKeys = Object.keys(fieldLabels).filter((key) => fields[key]?.value || missing.includes(key) || needConfirm.includes(key));
  const fieldsContainer = document.getElementById('bp-result-fields');
  fieldsContainer.innerHTML = orderedKeys.length
    ? orderedKeys.map((key) => {
        const field = fields[key] || { value: '', needConfirm: true, confidence: 0, source: '' };
        const isMissing = missing.includes(key);
        const isConfirm = field.needConfirm || needConfirm.includes(key);
        const borderColor = isMissing ? '#EF4444' : isConfirm ? '#FDBA74' : '#E5E7EB';
        const statusTag = isMissing
          ? '<span style="color:#EF4444;font-size:10px;">必填缺失</span>'
          : isConfirm
            ? '<span style="color:#F97316;font-size:10px;">待确认</span>'
            : `<span style="color:#22C55E;font-size:10px;">${Math.round((field.confidence || 0) * 100)}%</span>`;
        const inputId = `bp-field-${key}`;
        return `<div style="background:#F9FAFB;padding:8px;border-radius:4px;border:1px solid ${borderColor};" data-bp-field-key="${key}">
          <div style="display:flex;justify-content:space-between;gap:6px;margin-bottom:4px;">
            <span style="color:#6B7280;font-size:11px;">${escapeHtml(fieldLabels[key] || key)}</span>
            ${statusTag}
          </div>
          <input id="${inputId}" type="text" class="input-control" value="${escapeHtml(field.value || '')}" style="font-size:12px;width:100%;box-sizing:border-box;" data-bp-field-input="${key}">
          <div style="color:#9CA3AF;font-size:10px;margin-top:4px;">来源：${escapeHtml(field.source || 'BP')}</div>
        </div>`;
      }).join('')
    : '<div style="color:#9CA3AF;grid-column:1/-1;">暂无抽取字段</div>';

  // 绑定字段修改事件
  fieldsContainer.querySelectorAll('[data-bp-field-input]').forEach((input) => {
    input.addEventListener('change', function () {
      const key = this.dataset.bpFieldInput;
      if (!latestBpParse?.archive?.fields) return;
      latestBpParse.archive.fields[key] = {
        ...(latestBpParse.archive.fields[key] || {}),
        value: this.value,
        needConfirm: false,
        confidence: 1,
        source: '人工确认'
      };
    });
  });

  document.getElementById('bp-result-summary').textContent =
    generated.projectSummary || result.summary || '--';

  const highlights = generated.investmentHighlights || [];
  document.getElementById('bp-result-highlights').innerHTML = highlights.length
    ? highlights.map((item) => `<li>${escapeHtml(item.content)}${item.needConfirm ? ' <span style="color:#F97316;">(待确认)</span>' : ''}</li>`).join('')
    : '<li>暂无</li>';

  const risks = generated.riskPoints || [];
  document.getElementById('bp-result-risks').innerHTML = risks.length
    ? risks.map((item) => `<li>[${escapeHtml(item.riskLevel || 'medium')}] ${escapeHtml(item.content)}</li>`).join('')
    : '<li>暂无</li>';

  const supplements = generated.supplementMaterials || [];
  document.getElementById('bp-result-supplements').textContent =
    supplements.length ? supplements.join('、') : '--';

  const warnings = archive.warnings || [];
  document.getElementById('bp-result-warnings').innerHTML = warnings.length
    ? warnings.map((w) => `<div>· ${escapeHtml(w)}</div>`).join('')
    : '';

  const textPreview = result.textPreview || archive.textPreview || result.documentContent || '--';
  const paragraphCount = textPreview === '--' ? 0 : textPreview.split(/\n\s*\n/).filter((p) => p.trim()).length;
  document.getElementById('bp-result-text-len').textContent = `${result.textLength || archive.textLength || 0} 字`;
  document.getElementById('bp-result-text-lines').textContent = `${paragraphCount} 段`;
  document.getElementById('bp-result-text').innerHTML = formatBpTextPreview(textPreview);
}

function switchDdQuestionTab(tabId) {
  document.querySelectorAll('[data-ddq-tab]').forEach((tab) => {
    tab.classList.toggle('active', tab.dataset.ddqTab === tabId);
  });
  document.getElementById('ddq-tab-questions')?.classList.toggle('active', tabId === 'questions');
  document.getElementById('ddq-tab-closure')?.classList.toggle('active', tabId === 'closure');
}

function cacheDdClosureRecord(record) {
  if (!ddChecklistStore[record.projectId]) {
    ddChecklistStore[record.projectId] = {};
  }
  ddChecklistStore[record.projectId][record.itemKey] = record;
}

async function loadDdChecklistClosures(projectId) {
  if (!projectId || projectId === 'draft') return;
  const result = await requestRuntime(
    `/api/business/projects/${encodeURIComponent(projectId)}/dd-checklist`
  );
  ddChecklistStore[projectId] = {};
  (result.items || []).forEach(cacheDdClosureRecord);
}

async function upsertDdClosureRecord(projectId, itemKey, patch) {
  const result = await requestRuntime(
    `/api/business/projects/${encodeURIComponent(projectId)}/dd-checklist`,
    {
      method: 'PUT',
      body: JSON.stringify({ itemKey, ...patch })
    }
  );
  cacheDdClosureRecord(result.item);
  return result.item;
}

function ddClosureStatusLabel(status) {
  return ({
    pending: '待处理',
    requested: '已请求',
    received: '已收到',
    verified: '已核验',
    closed: '已关闭'
  })[status] || status;
}

function isDdClosureOverdue(item) {
  if (!item.dueDate || item.status === 'closed' || item.status === 'verified') return false;
  const today = new Date().toISOString().slice(0, 10);
  return item.dueDate < today;
}

function renderDdClosureList(projectId) {
  const container = document.getElementById('ddq-closure-list');
  if (!container) return;
  const store = ddChecklistStore[projectId] || {};
  const items = Object.values(store).sort((a, b) => {
    const pw = (p) => (p === 'P0' ? 0 : p === 'P1' ? 1 : 2);
    const pd = pw(a.priority) - pw(b.priority);
    if (pd !== 0) return pd;
    return String(a.dueDate || '').localeCompare(String(b.dueDate || ''));
  });

  const openP0 = items.filter((item) => item.priority === 'P0' && !['closed', 'verified'].includes(item.status)).length;
  const closed = items.filter((item) => ['closed', 'verified'].includes(item.status)).length;
  const overdue = items.filter(isDdClosureOverdue).length;

  document.getElementById('ddq-closure-p0-open').textContent = openP0;
  document.getElementById('ddq-closure-closed').textContent = closed;
  document.getElementById('ddq-closure-overdue').textContent = overdue;
  document.getElementById('ddq-closure-meta').textContent =
    items.length ? `共 ${items.length} 条 · 报告章节已映射` : '请先生成六维清单或点击「初始化闭环」';

  if (!items.length) {
    container.innerHTML = '<div style="padding:12px;font-size:10px;color:#6B7280;">暂无闭环条目。使用「六维清单（规则引擎）」或「初始化闭环」写入项目。</div>';
    return;
  }

  container.innerHTML = items.map((item) => `
    <div class="ddq-closure-item${isDdClosureOverdue(item) ? ' overdue' : ''}">
      <div>
        <span class="ddq-badge ${escapeHtml(item.priority)}">${escapeHtml(item.priority)}</span>
        <div style="font-size:9px;color:#8A95A5;margin-top:3px;">${escapeHtml(item.itemType === 'material' ? '资料' : '问题')}</div>
      </div>
      <div>
        <div style="font-weight:600;color:#0B1426;line-height:1.45;margin-bottom:3px;">${escapeHtml(item.title)}</div>
        <div style="color:#7B8491;font-size:9px;">
          ${escapeHtml(item.reportChapter || '--')} · 负责人 ${escapeHtml(item.owner || '--')}
          · 截止 ${escapeHtml(item.dueDate || '--')}
          ${isDdClosureOverdue(item) ? ' · <span style="color:#B8443F;">逾期</span>' : ''}
        </div>
      </div>
      <div class="ddq-closure-actions">
        <select data-dd-closure-status="${escapeHtml(item.itemKey)}" data-owner="${escapeHtml(item.owner || '')}">
          ${['pending', 'requested', 'received', 'verified', 'closed'].map((status) => `
            <option value="${status}"${item.status === status ? ' selected' : ''}>${ddClosureStatusLabel(status)}</option>
          `).join('')}
        </select>
      </div>
    </div>
  `).join('');
}

function renderDdQuestions(result) {
  latestDdQuestions = result;
  document.getElementById('ddq-empty').style.display = 'none';
  document.getElementById('ddq-report').classList.add('active');

  const info = result.basicInfo || {};
  const stats = result.statistics || {};
  document.getElementById('ddq-report-title').textContent =
    `${info.companyName || '项目'} · 尽调问题清单`;
  document.getElementById('ddq-report-meta').textContent = [
    info.industry,
    info.financingStage,
    info.financingAmount,
    `共 ${stats.totalQuestions || 0} 题`
  ].filter(Boolean).join(' · ');

  document.getElementById('ddq-stat-total').textContent = stats.totalQuestions || 0;
  document.getElementById('ddq-stat-p0').textContent = stats.p0 || 0;
  document.getElementById('ddq-stat-p1').textContent = stats.p1 || 0;
  document.getElementById('ddq-stat-p2').textContent = stats.p2 || 0;
  document.getElementById('ddq-stat-mat').textContent = stats.materialCount || 0;

  const overview = result.ddOverview || {};
  document.getElementById('ddq-overview-summary').textContent = overview.summary || '--';
  document.getElementById('ddq-overview-risks').innerHTML =
    (overview.highRiskAreas || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('') ||
    '<li>暂无高风险领域标注</li>';
  document.getElementById('ddq-overview-order').innerHTML =
    (overview.priorityOrder || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('') ||
    '<li>暂无优先顺序</li>';

  const dims = Array.isArray(result.questionDimensions) ? result.questionDimensions : [];
  document.getElementById('ddq-dim-list').innerHTML = dims.map((dim, index) => `
    <button class="ddq-dim-item${index === 0 ? ' active' : ''}" type="button" data-ddq-view-dim="${escapeHtml(dim.code)}">
      <div class="ddq-dim-name">${escapeHtml(dim.label)}</div>
      <div class="ddq-dim-sub">${dim.questionCount || 0} 题 · P0 ${dim.p0Count || 0}</div>
    </button>
  `).join('');

  const materials = Array.isArray(result.materialList) ? result.materialList : [];
  document.getElementById('ddq-material-list').innerHTML = materials.length
    ? materials.map((group) => `
        <div style="margin-bottom:7px;">
          <div style="font-size:10px;font-weight:600;color:#0B1426;margin-bottom:3px;">
            ${escapeHtml(group.category)}
            <span class="ddq-badge ${escapeHtml(group.priority || 'P1')}">${escapeHtml(group.priority || 'P1')}</span>
          </div>
          <div style="font-size:10px;color:#4B5563;line-height:1.5;">${escapeHtml((group.items || []).join('；'))}</div>
        </div>
      `).join('')
    : '<div style="font-size:10px;color:#6B7280;">暂无资料清单</div>';

  document.getElementById('ddq-report-disclaimer').textContent =
    result.disclaimer || '须经尽调负责人人工确认。';

  ddqActiveDimension = dims[0]?.code || '';
  renderDdQuestionDimension(result, ddqActiveDimension);
}

function renderDdQuestionDimension(result, dimensionCode) {
  const dims = Array.isArray(result.questionDimensions) ? result.questionDimensions : [];
  const dim = dims.find((item) => item.code === dimensionCode) || dims[0];
  document.querySelectorAll('#ddq-dim-list .ddq-dim-item').forEach((item) => {
    item.classList.toggle('active', item.dataset.ddqViewDim === dim?.code);
  });
  if (!dim) {
    document.getElementById('ddq-q-list').innerHTML = '<div style="font-size:10px;color:#6B7280;">暂无问题</div>';
    return;
  }
  document.getElementById('ddq-q-head-title').textContent = dim.label;
  document.getElementById('ddq-q-head-count').textContent = `${dim.questionCount || 0} 条`;
  const questions = Array.isArray(dim.questions) ? dim.questions : [];
  document.getElementById('ddq-q-list').innerHTML = questions.map((item) => `
    <div class="ddq-q-item">
      <div class="ddq-q-top">
        <span class="ddq-badge ${escapeHtml(item.priority)}">${escapeHtml(item.priority)}</span>
        <span class="ddq-badge ${escapeHtml(item.riskLevel)}">${item.riskLevel === 'high' ? '高风险' : item.riskLevel === 'low' ? '低风险' : '中风险'}</span>
        <span style="font-size:9px;color:#8A95A5;">${escapeHtml(item.verifyMethod || '')}</span>
        <span style="font-size:9px;color:#8A95A5;margin-left:auto;">${escapeHtml(item.ownerRole || '')}</span>
      </div>
      <div class="ddq-q-text">${escapeHtml(item.question)}</div>
      <div class="ddq-q-meta">
        主题：${escapeHtml(item.topic || '--')} · 目的：${escapeHtml(item.purpose || '--')}<br>
        报告章节：${escapeHtml(item.reportChapter || '--')} · 依据：${escapeHtml(item.basis || '--')}
        ${item.requiredMaterials?.length ? `<br>所需资料：${escapeHtml(item.requiredMaterials.join('、'))}` : ''}
      </div>
    </div>
  `).join('');
}

function renderContractReview(result) {
  latestContractReview = result;
  document.getElementById('contract-empty').style.display = 'none';
  document.getElementById('contract-compare-report').classList.remove('active');
  document.getElementById('contract-advanced-report').classList.remove('active');
  document.getElementById('contract-report').classList.add('active');
  document.getElementById('contract-report-title').textContent =
    result.contractType?.label || '合同审阅报告';

  const overview = result.overview || {};
  const deal = result.dealInfo || {};
  const parties = Array.isArray(overview.parties) && overview.parties.length
    ? overview.parties.join('、')
    : '合同主体待核验';
  const metaParts = [
    result.fileName,
    parties,
    overview.effectiveDate || '',
    `覆盖率 ${result.statistics?.catalogCoverage ?? '--'}%`,
    `${result.textLength || 0} 字`
  ].filter(Boolean);
  document.getElementById('contract-report-meta').textContent = metaParts.join(' · ');

  const statistics = result.statistics || {};
  document.getElementById('contract-stat-high').textContent = statistics.high || 0;
  document.getElementById('contract-stat-medium').textContent = statistics.medium || 0;
  document.getElementById('contract-stat-low').textContent = statistics.low || 0;
  document.getElementById('contract-stat-missing').textContent = statistics.missing || 0;

  const dealItems = [
    ['投资方', Array.isArray(deal.investors) && deal.investors.length ? deal.investors.join('、') : '--'],
    ['标的公司', deal.targetCompany || '--'],
    ['创始人', deal.founders || '--'],
    ['投资金额', deal.investmentAmount || overview.transactionAmount || '--'],
    ['投前估值', deal.preMoneyValuation || '--'],
    ['持股比例', deal.shareholdingRatio || '--'],
    ['支付方式', deal.paymentMethod || '--'],
    ['交割条件', deal.closingConditions || '--'],
    ['适用法律', overview.governingLaw || '--']
  ];
  document.getElementById('contract-deal-grid').innerHTML = dealItems.map(([label, value]) => `
    <div class="contract-deal-item"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>
  `).join('');

  const summary = result.reviewSummary || {};
  const findings = Array.isArray(summary.findings) ? summary.findings : [];
  const recommendations = Array.isArray(summary.recommendations) ? summary.recommendations : [];
  document.getElementById('contract-summary-box').innerHTML = `
    <div class="contract-summary-card">
      <h4>关键发现</h4>
      <ul>${findings.length ? findings.map((item) => `<li>${escapeHtml(item)}</li>`).join('') : '<li>暂无结构化发现</li>'}</ul>
    </div>
    <div class="contract-summary-card">
      <h4>核心建议</h4>
      <ul>${recommendations.length ? recommendations.map((item) => `<li>${escapeHtml(item)}</li>`).join('') : '<li>暂无结构化建议</li>'}</ul>
    </div>
  `;

  const clauses = Array.isArray(result.clauses) ? result.clauses : [];
  const missingClauses = Array.isArray(result.missingClauses) ? result.missingClauses : [];
  const list = document.getElementById('contract-clause-list');
  list.innerHTML = [
    ...clauses.map((clause, index) => `
      <button class="contract-clause-item${index === 0 ? ' active' : ''}" type="button" data-contract-clause="${index}">
        <div class="contract-clause-top">
          <span class="contract-risk-badge ${escapeHtml(clause.riskLevel)}">${contractRiskLabel(clause.riskLevel)}</span>
          <span class="contract-clause-title">${escapeHtml(clause.title)}</span>
        </div>
        <div class="contract-clause-location">${escapeHtml(clause.location)} · ${escapeHtml(clause.pageHint || '页码待核验')}</div>
      </button>
    `),
    ...missingClauses.map((clause, index) => `
      <button class="contract-clause-item" type="button" data-contract-missing="${index}">
        <div class="contract-clause-top">
          <span class="contract-risk-badge medium">缺失</span>
          <span class="contract-clause-title">${escapeHtml(clause.title)}</span>
        </div>
        <div class="contract-clause-location">合同原文中未发现</div>
      </button>
    `)
  ].join('');
  document.getElementById('contract-clause-count').textContent =
    `${clauses.length + missingClauses.length} 项`;
  document.getElementById('contract-report-disclaimer').textContent =
    result.disclaimer || '本结果须经法务人工复核。';

  const matchedRules = (result.riskIdentification || []).filter((item) => item.matched);
  const templateGaps = (result.templateComparison || []).filter((item) => item.status !== 'aligned');
  const revisions = result.revisionSuggestions || [];
  document.getElementById('contract-extra-panels').innerHTML = `
    <div class="contract-summary-card">
      <h4>风险规则命中（${matchedRules.length}）</h4>
      <ul>${matchedRules.length
        ? matchedRules.slice(0, 6).map((item) => `<li>[${escapeHtml(item.riskLevel)}] ${escapeHtml(item.title)}：${escapeHtml(item.evidence)}</li>`).join('')
        : '<li>暂无规则命中</li>'}</ul>
    </div>
    <div class="contract-summary-card">
      <h4>模板偏离 / 缺失（${templateGaps.length}）</h4>
      <ul>${templateGaps.length
        ? templateGaps.slice(0, 6).map((item) => `<li>[${escapeHtml(item.status)}] ${escapeHtml(item.title)}：${escapeHtml(item.gap)}</li>`).join('')
        : '<li>暂无显著偏离</li>'}</ul>
    </div>
    <div class="contract-summary-card" style="grid-column:1/-1;">
      <h4>修订建议</h4>
      <ul>${revisions.length
        ? revisions.slice(0, 8).map((item) => `<li>${escapeHtml(item)}</li>`).join('')
        : '<li>暂无修订建议</li>'}</ul>
    </div>
  `;

  const workpaper = result.reviewWorkpaper?.markdown || '';
  document.getElementById('contract-workpaper-card').style.display = workpaper ? 'block' : 'none';
  document.getElementById('contract-workpaper').textContent = workpaper;

  if (clauses[0]) {
    renderContractClauseDetail(clauses[0]);
  } else if (missingClauses[0]) {
    renderMissingContractClause(missingClauses[0]);
  } else {
    document.getElementById('contract-detail-location').textContent = '审阅结论';
    document.getElementById('contract-detail-scroll').innerHTML = `
      <div class="contract-detail-block">
        <div class="contract-detail-label">合同概况</div>
        <div class="contract-detail-text">${escapeHtml(overview.summary || '未识别到可展示的关键条款。')}</div>
      </div>
      <div class="contract-detail-block">
        <div class="contract-detail-label">审阅结论</div>
        <div class="contract-detail-text">${escapeHtml(result.reviewConclusion || '请人工复核合同全文。')}</div>
      </div>
    `;
  }
}

function renderContractCompare(result) {
  latestContractCompare = result;
  document.getElementById('contract-empty').style.display = 'none';
  document.getElementById('contract-report').classList.remove('active');
  document.getElementById('contract-advanced-report').classList.remove('active');
  document.getElementById('contract-compare-report').classList.add('active');
  document.getElementById('contract-compare-title').textContent = '版本对比报告';
  document.getElementById('contract-compare-meta').textContent =
    `${result.baseFileName || '旧版'} → ${result.targetFileName || '新版'}`;

  const statistics = result.statistics || {};
  document.getElementById('contract-cmp-total').textContent = statistics.total || 0;
  document.getElementById('contract-cmp-high').textContent = statistics.high || 0;
  document.getElementById('contract-cmp-added').textContent = statistics.added || 0;
  document.getElementById('contract-cmp-modified').textContent = statistics.modified || 0;
  document.getElementById('contract-cmp-removed').textContent = statistics.removed || 0;

  const suggestions = Array.isArray(result.keySuggestions) ? result.keySuggestions : [];
  document.getElementById('contract-compare-summary').innerHTML = `
    <div class="contract-summary-card">
      <h4>对比摘要</h4>
      <ul><li>${escapeHtml(result.compareSummary || '暂无摘要')}</li></ul>
    </div>
    <div class="contract-summary-card">
      <h4>核心建议</h4>
      <ul>${suggestions.length ? suggestions.map((item) => `<li>${escapeHtml(item)}</li>`).join('') : '<li>暂无建议</li>'}</ul>
    </div>
  `;

  const changes = Array.isArray(result.changes) ? result.changes : [];
  document.getElementById('contract-compare-list').innerHTML = changes.length
    ? changes.map((item) => `
        <div class="contract-diff-item ${escapeHtml(item.importance || 'medium')}">
          <div class="contract-diff-top">
            <span class="contract-risk-badge ${escapeHtml(item.importance || 'medium')}">${contractChangeLabel(item.changeType)}</span>
            <span class="contract-risk-badge ${escapeHtml(item.importance || 'medium')}">${contractRiskLabel(item.importance)}</span>
            <span class="contract-diff-title">${escapeHtml(item.title)}</span>
          </div>
          <div class="contract-diff-detail">${escapeHtml(item.changeDetail)}</div>
          ${item.baseExcerpt || item.targetExcerpt ? `
            <div class="contract-diff-quote">
              ${item.baseExcerpt ? `旧版：${escapeHtml(item.baseExcerpt)}<br>` : ''}
              ${item.targetExcerpt ? `新版：${escapeHtml(item.targetExcerpt)}` : ''}
            </div>
          ` : ''}
          ${item.suggestion ? `<div class="contract-diff-detail" style="margin-top:4px;">建议：${escapeHtml(item.suggestion)}</div>` : ''}
        </div>
      `).join('')
    : '<div class="contract-diff-item low"><div class="contract-diff-detail">未识别到实质性条款变更。</div></div>';

  document.getElementById('contract-compare-disclaimer').textContent =
    result.disclaimer || '本结果须经法务人工复核。';
}

function showContractAdvancedReport() {
  document.getElementById('contract-empty').style.display = 'none';
  document.getElementById('contract-report').classList.remove('active');
  document.getElementById('contract-compare-report').classList.remove('active');
  document.getElementById('contract-advanced-report').classList.add('active');
}

function renderContractConsistency(result) {
  latestContractAdvanced = result;
  showContractAdvancedReport();
  const statistics = result.statistics || {};
  document.getElementById('contract-adv-title').textContent = '条款一致性校验报告';
  document.getElementById('contract-adv-meta').textContent = [
    result.projectName,
    Array.isArray(result.fileNames) ? result.fileNames.join(' / ') : '',
    result.checkedAt || ''
  ].filter(Boolean).join(' · ');
  document.getElementById('contract-adv-stats').innerHTML = `
    <div class="contract-risk-stat">合计<strong>${statistics.total || 0}</strong></div>
    <div class="contract-risk-stat high">高风险<strong>${statistics.high || 0}</strong></div>
    <div class="contract-risk-stat medium">内部<strong>${statistics.internal || 0}</strong></div>
    <div class="contract-risk-stat">跨合同<strong>${statistics.crossContract || 0}</strong></div>
  `;
  document.getElementById('contract-adv-summary').innerHTML = `
    <div class="contract-summary-card" style="grid-column:1 / -1;">
      <h4>校验摘要</h4>
      <ul><li>${escapeHtml(result.summary || '暂无摘要')}</li></ul>
    </div>
  `;
  const issues = Array.isArray(result.issues) ? result.issues : [];
  document.getElementById('contract-adv-list').innerHTML = issues.length
    ? issues.map((item) => `
        <div class="contract-diff-item ${escapeHtml(item.severity || 'medium')}">
          <div class="contract-diff-top">
            <span class="contract-risk-badge ${escapeHtml(item.severity || 'medium')}">${item.scope === 'cross_contract' ? '跨合同' : '内部'}</span>
            <span class="contract-risk-badge ${escapeHtml(item.severity || 'medium')}">${contractRiskLabel(item.severity)}</span>
            <span class="contract-diff-title">${escapeHtml(item.topic)}</span>
          </div>
          <div class="contract-diff-detail">${escapeHtml(item.description)}</div>
          ${item.evidence?.length ? `<div class="contract-diff-quote">${item.evidence.map((line) => escapeHtml(line)).join('<br>')}</div>` : ''}
          ${item.suggestion ? `<div class="contract-diff-detail" style="margin-top:4px;">建议：${escapeHtml(item.suggestion)}</div>` : ''}
        </div>
      `).join('')
    : '<div class="contract-diff-item low"><div class="contract-diff-detail">未发现显著一致性冲突。</div></div>';
  document.getElementById('contract-adv-disclaimer').textContent =
    result.disclaimer || '本结果须经法务人工复核。';
}

function renderContractSupplement(result) {
  latestContractAdvanced = result;
  showContractAdvancedReport();
  const statistics = result.statistics || {};
  document.getElementById('contract-adv-title').textContent = '补充协议影响分析';
  document.getElementById('contract-adv-meta').textContent =
    `${result.mainFileName || '主协议'} ← ${result.supplementFileName || '补充协议'}`;
  document.getElementById('contract-adv-stats').innerHTML = `
    <div class="contract-risk-stat">影响<strong>${statistics.total || 0}</strong></div>
    <div class="contract-risk-stat high">高风险<strong>${statistics.high || 0}</strong></div>
    <div class="contract-risk-stat medium">新增<strong>${statistics.added || 0}</strong></div>
    <div class="contract-risk-stat">修改<strong>${statistics.modified || 0}</strong></div>
    <div class="contract-risk-stat">取代<strong>${statistics.superseded || 0}</strong></div>
  `;
  const suggestions = Array.isArray(result.keySuggestions) ? result.keySuggestions : [];
  document.getElementById('contract-adv-summary').innerHTML = `
    <div class="contract-summary-card">
      <h4>整体影响</h4>
      <ul><li>${escapeHtml(result.overallImpact || '暂无摘要')}</li></ul>
    </div>
    <div class="contract-summary-card">
      <h4>核心建议</h4>
      <ul>${suggestions.length ? suggestions.map((item) => `<li>${escapeHtml(item)}</li>`).join('') : '<li>暂无建议</li>'}</ul>
    </div>
  `;
  const impacts = Array.isArray(result.impacts) ? result.impacts : [];
  const impactLabel = {
    added: '新增',
    modified: '修改',
    superseded: '取代',
    clarified: '澄清'
  };
  document.getElementById('contract-adv-list').innerHTML = impacts.length
    ? impacts.map((item) => `
        <div class="contract-diff-item ${escapeHtml(item.riskLevel || 'medium')}">
          <div class="contract-diff-top">
            <span class="contract-risk-badge ${escapeHtml(item.riskLevel || 'medium')}">${impactLabel[item.impactType] || '影响'}</span>
            <span class="contract-risk-badge ${escapeHtml(item.riskLevel || 'medium')}">${contractRiskLabel(item.riskLevel)}</span>
            <span class="contract-diff-title">${escapeHtml(item.title)}</span>
          </div>
          <div class="contract-diff-detail">${escapeHtml(item.impactDetail)}</div>
          ${item.mainExcerpt || item.supplementExcerpt ? `
            <div class="contract-diff-quote">
              ${item.mainExcerpt ? `主协议：${escapeHtml(item.mainExcerpt)}<br>` : ''}
              ${item.supplementExcerpt ? `补充协议：${escapeHtml(item.supplementExcerpt)}` : ''}
            </div>
          ` : ''}
          ${item.suggestion ? `<div class="contract-diff-detail" style="margin-top:4px;">建议：${escapeHtml(item.suggestion)}</div>` : ''}
        </div>
      `).join('')
    : '<div class="contract-diff-item low"><div class="contract-diff-detail">未识别到显著影响项。</div></div>';
  document.getElementById('contract-adv-disclaimer').textContent =
    result.disclaimer || '本结果须经法务人工复核。';
}

function renderContractHistory(result) {
  latestContractAdvanced = result;
  showContractAdvancedReport();
  const statistics = result.statistics || {};
  document.getElementById('contract-adv-title').textContent = '历史合同对比报告';
  document.getElementById('contract-adv-meta').textContent = [
    `当前：${result.currentFileName || '--'}`,
    Array.isArray(result.historyFileNames) && result.historyFileNames.length
      ? `历史：${result.historyFileNames.join(' / ')}`
      : ''
  ].filter(Boolean).join(' · ');
  document.getElementById('contract-adv-stats').innerHTML = `
    <div class="contract-risk-stat">演变<strong>${statistics.total || 0}</strong></div>
    <div class="contract-risk-stat high">高重要<strong>${statistics.high || 0}</strong></div>
    <div class="contract-risk-stat medium">中重要<strong>${statistics.medium || 0}</strong></div>
    <div class="contract-risk-stat">低重要<strong>${statistics.low || 0}</strong></div>
  `;
  const suggestions = Array.isArray(result.keySuggestions) ? result.keySuggestions : [];
  document.getElementById('contract-adv-summary').innerHTML = `
    <div class="contract-summary-card">
      <h4>演变趋势</h4>
      <ul><li>${escapeHtml(result.trendSummary || '暂无摘要')}</li></ul>
    </div>
    <div class="contract-summary-card">
      <h4>核心建议</h4>
      <ul>${suggestions.length ? suggestions.map((item) => `<li>${escapeHtml(item)}</li>`).join('') : '<li>暂无建议</li>'}</ul>
    </div>
  `;
  const changes = Array.isArray(result.changes) ? result.changes : [];
  const historyChangeLabel = {
    added: '新增',
    modified: '修改',
    removed: '删除',
    reverted: '回退'
  };
  document.getElementById('contract-adv-list').innerHTML = changes.length
    ? changes.map((item) => `
        <div class="contract-diff-item ${escapeHtml(item.importance || 'medium')}">
          <div class="contract-diff-top">
            <span class="contract-risk-badge ${escapeHtml(item.importance || 'medium')}">${historyChangeLabel[item.changeType] || '变更'}</span>
            <span class="contract-risk-badge ${escapeHtml(item.importance || 'medium')}">${contractRiskLabel(item.importance)}</span>
            <span class="contract-diff-title">${escapeHtml(item.title)}</span>
          </div>
          <div class="contract-diff-detail">${escapeHtml(item.changeDetail)}</div>
          <div class="contract-diff-quote">对照历史：${escapeHtml(item.historyFileName || '--')}</div>
          ${item.suggestion ? `<div class="contract-diff-detail" style="margin-top:4px;">建议：${escapeHtml(item.suggestion)}</div>` : ''}
        </div>
      `).join('')
    : '<div class="contract-diff-item low"><div class="contract-diff-detail">相对历史版本未识别到显著演变。</div></div>';
  document.getElementById('contract-adv-disclaimer').textContent =
    result.disclaimer || '本结果须经法务人工复核。';
}

function renderContractClauseDetail(clause) {
  if (!clause) return;
  document.getElementById('contract-detail-location').textContent =
    `${clause.location || '位置待核验'} · ${clause.pageHint || '页码待核验'}`;
  document.getElementById('contract-detail-scroll').innerHTML = `
    <div class="contract-detail-title">
      <span class="contract-risk-badge ${escapeHtml(clause.riskLevel)}">${contractRiskLabel(clause.riskLevel)}</span>
      <span>${escapeHtml(clause.title)}</span>
    </div>
    ${clause.summary ? `
      <div class="contract-detail-block">
        <div class="contract-detail-label">条款摘要</div>
        <div class="contract-detail-text">${escapeHtml(clause.summary)}</div>
      </div>
    ` : ''}
    <div class="contract-detail-block">
      <div class="contract-detail-label">原文证据</div>
      <div class="contract-quote">${escapeHtml(clause.excerpt)}</div>
    </div>
    <div class="contract-detail-block">
      <div class="contract-detail-label">风险分析</div>
      <div class="contract-detail-text">${escapeHtml(clause.analysis || '待法务进一步分析。')}</div>
    </div>
    <div class="contract-detail-block">
      <div class="contract-detail-label">注意事项 / 优化建议</div>
      <div class="contract-detail-text">${escapeHtml(clause.suggestion || '建议结合交易背景人工确认。')}</div>
    </div>
    <div class="contract-detail-block">
      <div class="contract-detail-label">投资人保护立场</div>
      <div class="contract-detail-text">${escapeHtml(clause.standardPosition || '待结合机构条款库确认。')}</div>
    </div>
  `;
}

function renderMissingContractClause(clause) {
  if (!clause) return;
  document.getElementById('contract-detail-location').textContent = '合同原文中未发现';
  document.getElementById('contract-detail-scroll').innerHTML = `
    <div class="contract-detail-title">
      <span class="contract-risk-badge medium">缺失</span>
      <span>${escapeHtml(clause.title)}</span>
    </div>
    <div class="contract-missing">${escapeHtml(clause.risk || '该项投资人保护安排需进一步核验。')}</div>
    <div class="contract-detail-block">
      <div class="contract-detail-label">补充建议</div>
      <div class="contract-detail-text">${escapeHtml(clause.suggestion || '建议由项目负责人和法务确认是否需要补充。')}</div>
    </div>
  `;
}

function contractRiskLabel(level) {
  return level === 'high' ? '高风险' : level === 'low' ? '低风险' : '中风险';
}

function contractChangeLabel(type) {
  return type === 'added' ? '新增' : type === 'removed' ? '删除' : '修改';
}

/** 工作台首页：待办审批 / 待确认 AI 建议 / 活跃预警 / 最近项目 */
async function loadWorkbench() {
  const approvalsEl = document.getElementById('wb-approvals-count');
  if (approvalsEl) approvalsEl.textContent = '…';
  try {
    const approvals = await requestRuntime('/api/business/approvals?status=pending', { timeoutMs: 15000 });
    const list = Array.isArray(approvals) ? approvals : (approvals?.items || approvals?.approvals || []);
    if (approvalsEl) approvalsEl.textContent = String(list.length);
  } catch {
    if (approvalsEl) approvalsEl.textContent = '—';
  }

  const confirmEl = document.getElementById('wb-ai-confirm-count');
  if (confirmEl) {
    const prejudgeConfirm = Array.isArray(latestPreJudgment?.confirmCriteria)
      ? latestPreJudgment.confirmCriteria.length
      : 0;
    const bpConfirm = Object.values(latestBpParse?.archive?.fields || {})
      .filter((field) => field?.needConfirm).length;
    confirmEl.textContent = (latestPreJudgment || latestBpParse)
      ? String(prejudgeConfirm + bpConfirm)
      : '—';
  }

  const alertsEl = document.getElementById('wb-alerts-count');
  if (alertsEl) {
    alertsEl.textContent = Array.isArray(latestAlertRisks)
      ? String(latestAlertRisks.filter((item) => (item.closureStatus || item.status) !== 'resolved').length)
      : '—';
  }

  const projectsEl = document.getElementById('wb-projects-count');
  const recentEl = document.getElementById('wb-recent-projects');
  if (recentEl) recentEl.innerHTML = '正在加载…';
  try {
    const projects = await requestRuntime('/api/business/projects?limit=20', { timeoutMs: 15000 });
    const list = Array.isArray(projects) ? projects : [];
    if (projectsEl) projectsEl.textContent = String(list.length);
    if (recentEl) {
      recentEl.innerHTML = list.length
        ? list.slice(0, 5).map((project) => `
          <div class="wb-project-item" data-open-project="${escapeHtml(project.projectId)}" role="button" tabindex="0">
            <div>
              <div class="wb-project-name">${escapeHtml(project.companyName || project.projectName || project.projectId)}</div>
              <div class="wb-project-meta">${escapeHtml(project.industryName || '行业未识别')} · 评分 ${project.score ?? '--'} · ${escapeHtml((project.latestRunAt || project.updatedAt || '').replace('T', ' ').slice(0, 16))}</div>
            </div>
            <span class="wb-project-stage">${escapeHtml(stageLabel(project.stage))}</span>
          </div>
        `).join('')
        : '暂无项目。上传 BP 完成一次立项后会出现在这里。';
    }
  } catch (error) {
    if (projectsEl) projectsEl.textContent = '—';
    if (recentEl) recentEl.innerHTML = `无法加载项目列表：${escapeHtml(error.message)}`;
  }
}

async function loadProjectList() {
  const container = document.getElementById('project-list');
  if (container) container.innerHTML = '正在加载 Runtime 项目列表...';
  try {
    console.log('[project-list] 开始请求 /api/business/projects?limit=20');
    const projects = await requestRuntime('/api/business/projects?limit=20', { timeoutMs: 15000 });
    console.log('[project-list] 收到响应', Array.isArray(projects), projects?.length);
    if (!Array.isArray(projects) || !projects.length) {
      if (container) container.innerHTML = '暂无历史项目。完成一次立项后会出现在这里。';
      return;
    }
    if (container) container.innerHTML = projects.map(project => `
      <button data-open-project="${escapeHtml(project.projectId)}" type="button"
        style="width:100%;text-align:left;margin-bottom:8px;padding:10px;border:1px solid #E5E7EB;border-radius:5px;background:${project.projectId === currentProjectId ? '#F5F1EB' : '#fff'};cursor:pointer;">
        <div style="display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;">
          <strong style="color:#0B1426;">${escapeHtml(project.companyName || project.projectName)}</strong>
          <span class="badge badge-gold">${escapeHtml(stageLabel(project.stage))}</span>
        </div>
        <div style="color:#6B7280;font-size:11px;">
          ${escapeHtml(project.industryName || '行业未识别')}
          · 评分 ${project.score ?? '--'}
          · ${escapeHtml(decisionLabel(project.decisionResult))}
          · 报告 ${project.artifactCount ?? 0}
        </div>
        <div style="color:#9CA3AF;font-size:10px;margin-top:4px;">${escapeHtml((project.latestRunAt || project.updatedAt || '').replace('T', ' ').slice(0, 19))}</div>
      </button>
    `).join('');
  } catch (error) {
    console.error('[project-list] 加载失败', error);
    if (container) container.innerHTML = `无法加载项目列表：${escapeHtml(error.message)}`;
  }
}

async function openExistingProject(projectId) {
  const detail = await requestRuntime(`/api/business/projects/${encodeURIComponent(projectId)}`);
  currentProjectId = projectId;
  localStorage.setItem('pe-ai-current-project', currentProjectId);
  currentProjectInput = {
    companyName: detail.company?.companyName || '',
    industryName: detail.industry?.industryName || '',
    financingRound: '',
    investmentAmount: '',
    businessSummary: detail.company?.businessSummary || ''
  };
  // 打开项目时即加载预警闭环，确保 P10/P8 展示与服务端一致
  void loadPostRiskClosures(projectId).catch((error) => {
    console.warn('[risk-closure] 加载失败', error);
  });
  void loadDdChecklistClosures(projectId).then(() => {
    renderDdClosureList(projectId);
  }).catch((error) => {
    console.warn('[dd-closure] 加载失败', error);
  });
  renderRuntimeResult({
    ...detail,
    traces: detail.logs?.map(log => ({
      skillName: log.workflowId,
      durationMs: log.durationMs,
      status: log.status
    })) || []
  });
  renderReviewPage(detail, currentProjectInput);
}

function renderRuntimeResult(result) {
  const panel = document.getElementById('runtime-result-panel');
  const completedSkills = (result.traces || []).filter(trace => trace.status === 'success' || trace.status === 'failed');
  const approvalBlock = isAwaitingApproval(result)
    ? buildApprovalBannerHtml(result.runId)
    : '';
  panel.innerHTML = `
    <div class="panel-header" style="background:#0B1426;color:#fff;border-bottom-color:#1a2a4a;">
      <span style="color:#C5A55A;margin-right:6px;">AI</span> Runtime 执行结果
    </div>
    <div style="padding:14px;color:#fff;">
      ${approvalBlock}
      <div style="font-size:11px;color:#8A95A5;margin-bottom:8px;">项目 ID</div>
      <div style="font-size:12px;color:#C5A55A;word-break:break-all;margin-bottom:12px;">${escapeHtml(result.projectId)}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;">
        <div class="ai-card"><div style="font-size:10px;color:#8A95A5;">WORKFLOW</div><strong style="font-size:11px;">${escapeHtml(result.workflowId || '')}</strong></div>
        <div class="ai-card"><div style="font-size:10px;color:#8A95A5;">EVALUATION</div><strong style="font-size:18px;color:#22C55E;">${result.evaluation?.score ?? '--'}</strong></div>
      </div>
      <div style="font-size:12px;color:#C5A55A;font-weight:600;margin-bottom:6px;">执行链路</div>
      ${completedSkills.length
        ? completedSkills.map(trace => `<div style="font-size:11px;color:#D1D5DB;padding:4px 0;border-bottom:1px solid #1a2a4a;">${escapeHtml(trace.skillName)} <span style="float:right;color:${trace.status === 'failed' ? '#F97316' : '#22C55E'};">${trace.durationMs}ms</span></div>`).join('')
        : '<div style="font-size:11px;color:#8A95A5;">历史回看模式下显示运行记录摘要。</div>'}
      <div class="ai-card" style="margin-top:12px;background:#C5A55A15;">
        <div style="font-size:11px;color:#C5A55A;font-weight:600;margin-bottom:4px;">决策结果</div>
        <p style="font-size:11px;color:#D1D5DB;line-height:1.6;">${escapeHtml(formatDecision(result.decision))}</p>
      </div>
    </div>`;
  if (typeof applyRbacUiGates === 'function') applyRbacUiGates();
}

function renderReviewPage(result, input) {
  const scoreEl = document.getElementById('review-score');
  const denomEl = document.getElementById('review-score-denom');
  const dims = result.evaluation?.dimensions || {};
  // 六维数据：优先 evaluation.dimensions.sixDimensions（后端已持久化）；
  // 同一会话内旧数据缺失时兜底 latestPreJudgment
  let sixDims = Array.isArray(dims.sixDimensions) && dims.sixDimensions.length
    ? dims.sixDimensions
    : null;
  if (!sixDims && latestPreJudgment?.dimensions?.length) {
    sixDims = latestPreJudgment.dimensions.map((d) => ({
      dimensionId: d.dimensionId,
      name: d.name,
      score: d.score,
      maxScore: d.maxScore,
      assessedCount: d.assessedCount ?? 0,
    }));
  }
  let pjTotal = dims.preJudgmentTotal && Number.isFinite(dims.preJudgmentTotal.score)
    ? dims.preJudgmentTotal
    : null;
  if (!pjTotal && sixDims?.length) {
    pjTotal = {
      score: sixDims.reduce((sum, d) => sum + (Number(d.score) || 0), 0),
      maxScore: sixDims.reduce((sum, d) => sum + (Number(d.maxScore) || 0), 0),
      assessedCount: sixDims.reduce((sum, d) => sum + (Number(d.assessedCount) || 0), 0),
    };
  }
  const hasPjTotal = Boolean(pjTotal && pjTotal.maxScore > 0 && pjTotal.assessedCount > 0);
  // 综合评分：优先前置研判六维总分；无研判结果时退回 Workflow 完成度启发式
  const heuristicScore = result.evaluation?.score;
  const score = hasPjTotal ? pjTotal.score : heuristicScore;
  const hasScore = Number.isFinite(score);
  scoreEl.textContent = hasScore ? String(score) : '';
  if (denomEl) denomEl.style.display = hasScore ? 'inline' : 'none';
  scoreEl.title = hasPjTotal
    ? `综合分 ${pjTotal.score}/${pjTotal.maxScore} · 前置研判六维总分（已评 ${pjTotal.assessedCount}/50 项指标）`
    : hasScore
      ? `综合分 ${score}/100 · Workflow 完成度启发式（尚无前置研判总分）`
      : '尚未打分';
  const sourceEl = document.getElementById('review-score-source');
  if (sourceEl) {
    sourceEl.textContent = hasPjTotal
      ? `综合分 = 前置研判六维总分 · 已评 ${pjTotal.assessedCount}/50 项指标`
      : Number.isFinite(heuristicScore)
        ? '综合分 = Workflow 完成度启发式 · 完成前置研判评分后自动替换为六维总分'
        : '综合评分尚未生成；完成前置研判评分或运行 Workflow 后展示。';
  }
  // 可解释性：仅展示 buildEvaluation 产出的真实 checks
  const checksEl = document.getElementById('review-score-checks');
  if (checksEl) {
    const checks = Array.isArray(result.evaluation?.checks) ? result.evaluation.checks.filter(Boolean) : [];
    checksEl.innerHTML = checks.length
      ? `<div style="font-size:10px;color:#6B7280;margin-bottom:4px;">Workflow 完成度检查项${hasPjTotal ? '（不影响综合分）' : ''}</div>` +
        checks.map((item) => `<div>· ${escapeHtml(item)}</div>`).join('')
      : '<div style="color:#9CA3AF;">运行后展示启发式检查项（决策/交付物/风险/证据/Skill 等）。</div>';
  }
  // 六维分直接来自前置研判；旧记录回退四维映射；缺失保持 --，不编造
  renderReviewDimensions(dims, sixDims);
  document.getElementById('review-decision').textContent = decisionLabel(result.decision?.result);
  const decisionEl = document.getElementById('review-decision');
  if (isAwaitingApproval(result)) {
    decisionEl.style.background = '#F59E0B';
  } else if (result.decision?.result === 'reject' || result.decision?.result === 'pause') {
    decisionEl.style.background = '#EF4444';
  } else {
    decisionEl.style.background = '#22C55E';
  }
  updateP2ApprovalBanner(result);
  document.getElementById('review-company').textContent = result.company?.companyName || input.companyName || '--';
  document.getElementById('review-industry').textContent = result.industry?.industryName || input.industryName || '--';
  document.getElementById('review-round').textContent = input.financingRound || '--';
  document.getElementById('review-amount').textContent = input.investmentAmount ? `${input.investmentAmount} 万元` : '--';
  document.getElementById('review-summary').textContent = [
    result.company?.businessSummary || input.businessSummary,
    formatDecision(result.decision)
  ].filter(Boolean).join(' ');
  document.getElementById('workflow-actions').style.display = 'flex';
  document.getElementById('p2-empty-actions').style.display = 'none';
  updateStageProjectLabels(result);

  const risks = result.risks || [];
  const riskPanel = document.getElementById('review-risk-panel');
  if (riskPanel) {
    riskPanel.innerHTML = `
      <div class="p2-card-head">
        <span>Runtime 风险扫描</span>
        <span class="p2-count-badge" style="color:${risks.length ? '#F97316' : '#6B7280'};">${risks.length || 0} 项需关注</span>
      </div>
      <div class="p2-risk-grid">
        ${risks.length
          ? risks.map(risk => `<div class="p2-risk-card ${risk.severity === 'high' ? 'high' : risk.severity === 'low' ? 'low' : 'medium'}"><div class="p2-risk-level">${escapeHtml(risk.category)} · ${escapeHtml(risk.severity)}</div><div>${escapeHtml(risk.description)}</div></div>`).join('')
          : '<div style="font-size:11px;color:#6B7280;">当前 Workflow 未识别到风险项。</div>'}
      </div>`;
  }

  renderReviewExternalEvidence(result);

  const logs = result.logs || [];
  document.getElementById('artifact-list').innerHTML = (result.artifacts || [])
    .map(artifact => `
      <a href="${runtimeBaseUrl}${artifact.uri}" target="_blank" rel="noopener" class="p2-artifact-link">
        <span>${escapeHtml(artifact.title)}</span>
        <strong style="color:#C5A55A;">下载 Markdown</strong>
      </a>`)
    .join('') || '<div style="font-size:11px;color:#6B7280;">暂无已生成报告。</div>';
  document.getElementById('run-history').innerHTML = `
    <div class="p2-run-history-title">运行历史</div>
    ${logs.length
      ? logs.map(log => `
        <div class="p2-run-item">
          <span>${escapeHtml(log.workflowId)} · ${escapeHtml(decisionLabel(log.decisionResult))}</span>
          <span style="display:flex;align-items:center;gap:6px;">
            <span style="color:${log.status === 'failed' ? '#EF4444' : (log.status === 'awaiting_approval' || log.decisionResult === 'pending_approval' ? '#F59E0B' : '#22C55E')};">${escapeHtml(log.status)} · ${log.durationMs}ms</span>
            ${(log.status === 'failed' || log.status === 'running' || log.status === 'awaiting_approval' || log.decisionResult === 'pending_approval')
              ? `<button type="button" class="btn-primary" data-resume-run="${escapeHtml(log.runId)}" style="padding:2px 8px;font-size:10px;">${log.status === 'awaiting_approval' || log.decisionResult === 'pending_approval' ? '确认并续跑' : '断点续跑'}</button>`
              : ''}
          </span>
        </div>`).join('')
      : '<div style="font-size:11px;color:#6B7280;">本次执行尚未写入运行历史，或项目刚创建。</div>'}`;
  applyRbacUiGates();
}

/**
 * 初评页展示外部工商证据与来源引用；无证据时如实说明原因。
 */
function renderReviewExternalEvidence(result) {
  const evidenceList = result.evidence
    || result.context?.evidence
    || [];
  const externalItems = (Array.isArray(evidenceList) ? evidenceList : [])
    .filter((item) => item && (item.source === 'xiniu' || item.source === 'qichacha'));
  const statusEl = document.getElementById('review-xiniu-status');
  const countEl = document.getElementById('review-xiniu-count');
  const bodyEl = document.getElementById('review-external-evidence');
  if (!bodyEl) return;

  if (countEl) {
    countEl.textContent = externalItems.length
      ? `${externalItems.length} 条引用`
      : '无外部证据';
  }
  if (statusEl) {
    statusEl.textContent = externalItems.length
      ? `已接入 · ${externalItems.length} 条`
      : (latestXiniuStatus && latestXiniuStatus.configured === false
        ? '未配置'
        : '本次无结果');
  }

  if (!externalItems.length) {
    const hint = latestXiniuStatus && latestXiniuStatus.configured === false
      ? '外部工商数据未配置：请在本地 .env 设置 XINIU_API_KEY 和/或 QCC_MCP_AUTHORIZATION（或 QCC_API_TOKEN）。不会编造工商摘要。'
      : '本次初评证据链中无外部工商结果（可能未命中企业名、上游空结果，或尚未完成 Workflow）。';
    bodyEl.innerHTML = `<div style="color:#6B7280;">${escapeHtml(hint)}</div>`;
    return;
  }

  bodyEl.innerHTML = externalItems.map((item) => `
    <div style="margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid #F3F4F6;">
      <div style="display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;">
        <strong style="color:#0B1426;">来源 · ${escapeHtml(item.source || 'external')}</strong>
        <span style="color:#9CA3AF;">置信度 ${escapeHtml(String(item.confidence ?? '--'))}</span>
      </div>
      <div style="color:#4B5563;white-space:pre-wrap;">${escapeHtml(excerptText(item.description || '', 800))}</div>
      <div style="margin-top:4px;font-size:10px;color:#9CA3AF;">evidenceId: ${escapeHtml(item.evidenceId || '')}</div>
    </div>
  `).join('');
}

function excerptText(text, maxLen) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLen) return normalized;
  return `${normalized.slice(0, maxLen - 1)}…`;
}

function highlightNumbers(html) {
  return html.replace(/(\d[\d,]*(?:\.\d+)?)(%|万|亿|元|万元|亿元|人|个|年|月|日|次|轮|倍)?/g, '<span class="bp-text-number">$1$2</span>');
}

function formatBpTextPreview(text) {
  if (!text || text === '--') {
    return '<div class="bp-text-placeholder">暂无文本</div>';
  }
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());
  if (paragraphs.length === 0) {
    return '<div class="bp-text-placeholder">暂无文本</div>';
  }
  return paragraphs.map((para) => {
    const trimmed = para.trim();
    const firstLine = trimmed.split('\n')[0].trim();

    // 标题：章节、数字序号、项目符号等
    if (/^(第[一二三四五六七八九十\d]+[章节部]|项目|公司|产品|市场|财务|团队|风险|融资|投资|商业模式|竞争优势|发展|规划|附录|参考资料|[\d一二三四五六七八九十]+[、.．]|[（(][\d一二三四五六七八九十]+[）)]|【.+】|◆|■|★|▎)/.test(firstLine)) {
      return `<div class="bp-text-heading">${escapeHtml(trimmed)}</div>`;
    }

    // 列表项
    if (/^[\s]*[•·\-–—*]\s+/.test(trimmed)) {
      return `<div class="bp-text-bullet">${highlightNumbers(escapeHtml(trimmed.replace(/^[\s]*[•·\-–—*]\s+/, '')))}</div>`;
    }

    // 字段名：值（冒号分隔）
    const colon = trimmed.indexOf('：') >= 0 ? trimmed.indexOf('：') : trimmed.indexOf(':');
    if (colon > 0 && colon < trimmed.length - 1) {
      const field = trimmed.slice(0, colon + 1);
      const value = trimmed.slice(colon + 1);
      return `<div class="bp-text-paragraph"><span class="bp-text-field">${escapeHtml(field)}</span>${highlightNumbers(escapeHtml(value))}</div>`;
    }

    return `<div class="bp-text-paragraph">${highlightNumbers(escapeHtml(trimmed))}</div>`;
  }).join('');
}

/**
 * 渲染初评六维分：直接展示前置研判六维原始分；
 * 旧记录回退四维映射；无真实分值时保持 --，不编造。
 */
function renderReviewDimensions(dimensions, sixDims) {
  const grid = document.getElementById('review-dim-grid');
  const footnote = document.getElementById('review-dim-footnote');
  if (!grid) return;
  const itemHtml = (name, score, maxScore, assessedCount) => {
    const has = assessedCount > 0 && Number.isFinite(score) && maxScore > 0;
    const pct = has ? Math.max(0, Math.min(100, Math.round((score / maxScore) * 100))) : 0;
    const tip = has
      ? `${name}：${score}/${maxScore} 分 · 已评 ${assessedCount} 项指标`
      : `${name}：该维度暂无已评指标`;
    return `
      <div class="p2-dim-item" title="${escapeHtml(tip)}">
        <span class="p2-dim-label">${escapeHtml(name)}</span>
        <strong class="p2-dim-score">${has
          ? `${score}<span style="font-size:10px;color:#9CA3AF;font-weight:400;">/${maxScore}</span>`
          : '--'}</strong>
        ${has ? `<div style="height:3px;border-radius:2px;background:#E5E7EB;margin-top:4px;"><div style="height:100%;width:${pct}%;border-radius:2px;background:#C5A55A;"></div></div>` : ''}
      </div>`;
  };
  if (sixDims?.length) {
    grid.innerHTML = sixDims
      .map((d) => itemHtml(d.name, Number(d.score), Number(d.maxScore), Number(d.assessedCount) || 0))
      .join('');
    const covered = sixDims.filter((d) => (Number(d.assessedCount) || 0) > 0).length;
    if (footnote) {
      const parts = [`来源：前置研判六维 · 已覆盖 ${covered}/6 个维度`];
      if (dimensions?.preJudgmentRunId) parts.push(`runId ${dimensions.preJudgmentRunId}`);
      footnote.textContent = parts.join(' · ');
    }
    return;
  }
  // 旧记录：仅有四维映射数据
  const legacy = [dimensions?.tech, dimensions?.market, dimensions?.team, dimensions?.finance].filter(Boolean);
  if (legacy.length) {
    grid.innerHTML = legacy
      .map((d) => itemHtml(d.label || d.sourceDimensionName || '', d.score, d.maxScore, d.assessedCount ?? 0))
      .join('');
    if (footnote) footnote.textContent = `来源：前置研判（旧版四维映射） · 已映射 ${legacy.length}/4 维`;
    return;
  }
  grid.innerHTML = '<div style="font-size:11px;color:#9CA3AF;grid-column:1/-1;">暂无前置研判维度分；完成前置研判评分后自动带入。</div>';
  if (footnote) footnote.textContent = '尚未挂载前置研判维度分；有评估结果后才会填入，不会用 0 占位。';
}

function setRuntimeProgress(percent) {
  const progress = document.getElementById('runtime-progress');
  if (progress) progress.style.width = `${percent}%`;
}

function showRuntimeError(message) {
  const panel = document.getElementById('runtime-result-panel');
  if (panel) {
    panel.innerHTML = `<div class="panel-header" style="background:#0B1426;color:#fff;border-bottom-color:#1a2a4a;">Runtime 错误</div><div style="padding:16px;color:#FCA5A5;font-size:12px;line-height:1.6;">${escapeHtml(message)}</div>`;
  }
  const p1rError = document.getElementById('bp-result-error');
  if (p1rError) {
    p1rError.textContent = message;
    p1rError.style.display = 'block';
  }
}

function formatDecision(decision) {
  if (!decision) return 'Workflow 已执行，暂无决策输出。';
  return [...(decision.reasons || []), ...(decision.nextActions || []).map(item => `下一步：${item}`)].join(' ');
}

function stageLabel(stage) {
  const labels = {
    initial_review: '初评',
    due_diligence: '尽调',
    investment_committee: '投委会',
    post_investment: '投后',
    exit: '退出'
  };
  return labels[stage] || stage || '未知阶段';
}

function decisionLabel(result) {
  const labels = {
    continue_initial_review: '继续完善初评',
    continue_due_diligence: '进入尽调',
    enter_investment_committee: '进入投委会',
    approve: '批准投资',
    continue_monitoring: '继续投后监控',
    recommend_exit: '建议退出',
    pause: '暂停',
    reject: '否决',
    pending_approval: '等待人工审批'
  };
  return labels[result] || result || '等待决策';
}


/**
 * 刷新外部数据源配置状态（烯牛 / 企查查），同步到立项页与情报台徽章。
 */
async function refreshXiniuStatus() {
  const badges = [
    document.getElementById('xiniu-status-badge'),
    document.getElementById('intel-status-badge')
  ].filter(Boolean);
  const messages = [
    document.getElementById('xiniu-status-message'),
    document.getElementById('intel-status-message')
  ].filter(Boolean);
  const meta = document.getElementById('intel-status-meta');
  try {
    const result = await requestRuntime('/api/business/external-data/status');
    latestXiniuStatus = result;
    const configured = Boolean(result.configured);
    const label = configured ? '已配置' : '未配置';
    const msg = result.message || (configured
      ? '外部数据已配置'
      : '外部数据未配置：请设置 XINIU_API_KEY 和/或 QCC_MCP_AUTHORIZATION（或 QCC_API_TOKEN）');
    for (const badge of badges) {
      badge.textContent = label;
      badge.className = configured ? 'badge badge-green' : 'badge badge-orange';
    }
    for (const el of messages) {
      el.textContent = msg;
    }
    if (meta) {
      const providers = Array.isArray(result.providers) ? result.providers : [];
      if (providers.length > 0) {
        meta.textContent = providers.map((item) => {
          const name = item.provider === 'qichacha' ? '企查查' : '烯牛';
          const flag = item.configured ? '已配置' : '未配置';
          const endpoint = item.mcpUrl || result.mcpUrl || '--';
          return `${name}=${flag}(${endpoint})`;
        }).join(' · ');
      } else {
        meta.textContent = `provider=${result.provider || 'multi'} · mcpUrl=${result.mcpUrl || '--'}`;
      }
    }
    return result;
  } catch (error) {
    latestXiniuStatus = null;
    for (const badge of badges) {
      badge.textContent = '离线';
      badge.className = 'badge badge-orange';
    }
    for (const el of messages) {
      el.textContent = `无法读取外部数据状态：${error.message}`;
    }
    if (meta) meta.textContent = '';
    return null;
  }
}

function providerDisplayName(provider) {
  if (provider === 'qichacha') return '企查查';
  if (provider === 'xiniu') return '烯牛';
  if (provider === 'wind') return 'Wind';
  if (provider === 'ifind') return 'iFinD';
  if (provider === 'local') return '本地库';
  if (provider === 'manual') return '人工录入';
  if (provider === 'auto') return '自动';
  return provider || '外部数据';
}

/**
 * 刷新 DataProviderRouter 数据源目录（p16 数据源管理页）。
 */
async function refreshDataProviders() {
  const badge = document.getElementById('datasources-status-badge');
  const summary = document.getElementById('datasources-summary');
  const cacheMeta = document.getElementById('datasources-cache-meta');
  const tableBody = document.getElementById('datasources-table-body');
  try {
    const result = await requestRuntime('/api/business/data-providers/status');
    const configured = Boolean(result.configured);
    if (badge) {
      badge.textContent = configured ? '已配置' : '未配置';
      badge.className = configured ? 'badge badge-green' : 'badge badge-orange';
    }
    if (summary) {
      summary.textContent = result.message || (configured
        ? '至少一项远程数据源已配置'
        : '外部数据未配置：请设置 XINIU_API_KEY / QCC_* / IFIND_* / WIND_* 至少一项');
    }
    if (cacheMeta && result.cache) {
      const ttlHours = Math.round((result.cache.ttlMs || 0) / 3600000);
      cacheMeta.textContent = `缓存：${result.cache.enabled ? '已启用' : '未启用'} · TTL ${ttlHours}h`;
    }
    if (tableBody) {
      const providers = Array.isArray(result.providers) ? result.providers : [];
      if (providers.length === 0) {
        tableBody.innerHTML = '<tr><td colspan="5" style="color:#9CA3AF;">暂无 Provider 注册</td></tr>';
      } else {
        tableBody.innerHTML = providers.map((item) => {
          const caps = Array.isArray(item.capabilities) ? item.capabilities.join(', ') : '--';
          const flag = item.configured ? '已配置' : '未配置';
          const flagClass = item.configured ? 'badge badge-green' : 'badge badge-orange';
          const endpoint = item.endpointHint || '--';
          return `<tr>
            <td>${escapeHtml(providerDisplayName(item.provider))}</td>
            <td><span class="${flagClass}">${flag}</span></td>
            <td><code>${escapeHtml(caps)}</code></td>
            <td>${escapeHtml(item.message || '')}</td>
            <td style="font-size:10px;color:#6B7280;">${escapeHtml(endpoint)}</td>
          </tr>`;
        }).join('');
      }
    }
    return result;
  } catch (error) {
    if (badge) {
      badge.textContent = '离线';
      badge.className = 'badge badge-orange';
    }
    if (summary) summary.textContent = `无法读取数据源状态：${error.message}`;
    if (cacheMeta) cacheMeta.textContent = '';
    if (tableBody) {
      tableBody.innerHTML = `<tr><td colspan="5" style="color:#DC2626;">${escapeHtml(error.message)}</td></tr>`;
    }
    return null;
  }
}

function providerCiteText(provider) {
  if (provider === 'qichacha') {
    return '引用：企查查 Agent MCP get_company_by_query + get_company_registration_info · 不构成投资建议';
  }
  return '引用：烯牛 MCP get_company_info · 不构成投资建议';
}

async function ensureExternalIntelLoaded() {
  await refreshXiniuStatus();
  const companyInput = document.getElementById('intel-company-input');
  if (companyInput && !companyInput.value.trim()) {
    const hint = currentProjectInput?.companyName
      || latestCopilotContext?.companyName
      || '';
    if (hint) companyInput.value = hint;
  }
}

/** 关键事实字段：仅展示响应中真实存在的值 */
const INTEL_FACT_SPECS = [
  { label: '公司工商名称', keys: ['公司工商名称', '企业名称', '公司名称', '公司全称', 'firm_name', 'companyName', 'format_name'] },
  { label: '企业简称', keys: ['企业简称', '公司简称'] },
  { label: '英文名', keys: ['英文名', '英文名称', 'englishName'] },
  { label: '统一社会信用代码', keys: ['统一社会信用代码', '信用代码', '社会信用代码', 'unifiedSocialCreditCode', 'creditNo', 'credit_code', 'uscc', 'creditCode'] },
  { label: '法定代表人', keys: ['法定代表人', '法定代表', '法人代表', '法人', 'legalRepresentative', 'operName', 'legal_person', 'legalPerson'] },
  { label: '实际控制人', keys: ['实际控制人', '实际控制人名称', '实控人'] },
  { label: '注册资本', keys: ['注册资本', '注册资金', 'registeredCapital', 'registCapi', 'regCapital', 'registered_capital'] },
  { label: '实缴资本', keys: ['实缴资本', '实收资本', 'actualCapi', 'paidInCapital'] },
  { label: '成立日期', keys: ['成立日期', '成立时间', '注册日期', 'foundedAt', 'startDate', 'establishDate', 'found_date'] },
  { label: '核准日期', keys: ['核准日期', 'checkDate', 'approvalDate'] },
  { label: '经营状态', keys: ['经营状态', '登记状态', '企业状态', 'status', 'regStatus', 'business_status'] },
  { label: '企业类型', keys: ['企业类型', '公司类型', '经济类型', 'econKind', 'companyType', 'org_type'] },
  { label: '营业期限', keys: ['营业期限', '经营期限', 'businessTerm'] },
  { label: '人员规模', keys: ['人员规模', '员工人数'] },
  { label: '社保人数', keys: ['社保人数', '参保人数', 'socialSecurityCount'] },
  { label: '国民经济行业', keys: ['国民经济行业', '国标行业', '所属行业', '行业', '企查查行业', 'industry', 'domain'] },
  { label: '烯牛行业', keys: ['烯牛行业'] },
  { label: '战略新兴产业', keys: ['战略新兴产业'] },
  { label: '所属地区', keys: ['所属地区', '行政区划'] },
  { label: '登记机关', keys: ['登记机关', 'registrationAuthority'] },
  { label: '注册地址', keys: ['注册地址', '企业地址', '住所', 'address', 'regLocation', 'reg_address'] },
  { label: '年报地址', keys: ['年报地址'] },
  { label: '年报电话', keys: ['年报电话'] }
];

/** 长文本区块：经营范围 / 简介 / 股东高管摘要等 */
const INTEL_TEXT_SPECS = [
  { label: '经营范围', keys: ['经营范围', 'businessScope', 'scope', 'business_scope'] },
  { label: '企业简介', keys: ['企业简介', '公司简介', '简介', 'description', 'summary', 'companyProfile', 'brief'] },
  { label: '股东摘要', keys: ['股东摘要', '股东信息'] },
  { label: '高管摘要', keys: ['高管摘要', '主要人员信息', '主要人员'] }
];

const INTEL_WRAP_KEYS = new Set([
  'result', 'data', 'company', 'basicInfo', 'BasicInfo', 'profile', 'content', 'payload', 'info'
]);

function setIntelResultMessage(body, message, kind) {
  if (!body) return;
  const safe = escapeHtml(message);
  if (kind === 'error') {
    body.className = '';
    body.innerHTML = `<div class="intel-result-error">${safe}</div>`;
    return;
  }
  body.className = 'intel-result-empty';
  body.textContent = message;
}

/**
 * 尝试把 profile / text / structuredContent 解析为键值对象；解析失败返回空对象，不编造字段。
 * 兼容：对象、JSON 字符串、markdown 围栏 JSON、标题+Python 单引号 dict。
 */
function parseIntelFieldMap(result) {
  const map = {};
  const merge = (source) => {
    if (source === null || source === undefined) return;
    if (typeof source === 'string') {
      const parsed = tryParseIntelLooseObject(source);
      if (parsed) {
        merge(parsed);
        return;
      }
      const kv = parseIntelKeyValueText(source);
      for (const [key, value] of Object.entries(kv)) {
        if (map[key] === undefined) map[key] = value;
      }
      return;
    }
    if (Array.isArray(source)) {
      for (const item of source) merge(item);
      return;
    }
    if (typeof source !== 'object') return;

    for (const [key, value] of Object.entries(source)) {
      if (value === null || value === undefined) continue;
      if (typeof value === 'string') {
        const embedded = tryParseIntelLooseObject(value);
        if (embedded) {
          merge(embedded);
          continue;
        }
        const normalized = normalizeIntelValue(value);
        if (normalized !== undefined && map[key] === undefined) {
          map[key] = normalized;
        }
        continue;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        if (map[key] === undefined) map[key] = String(value);
        continue;
      }
      if (Array.isArray(value)) {
        const joined = normalizeIntelValue(value);
        if (joined !== undefined && map[key] === undefined) {
          map[key] = joined;
        } else {
          merge(value);
        }
        continue;
      }
      if (typeof value === 'object') {
        merge(value);
      }
    }
  };

  // 优先使用后端归一化 profile；仍解析 text / structuredContent 作为兜底
  merge(result?.profile);
  merge(result?.structuredContent);
  merge(result?.text);

  // 去掉超长包装键 blob，避免主视图误展示
  for (const key of INTEL_WRAP_KEYS) {
    if (map[key] !== undefined && String(map[key]).length > 120) {
      delete map[key];
    }
  }

  const lowerIndex = {};
  for (const [key, value] of Object.entries(map)) {
    lowerIndex[String(key).toLowerCase()] = value;
  }
  return { map, lowerIndex };
}

function normalizeIntelValue(value) {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '--' || trimmed === '无' || trimmed === 'null' || trimmed === 'undefined') {
      return undefined;
    }
    return trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          return String(item).trim();
        }
        if (item && typeof item === 'object') {
          for (const key of ['股东名称', '姓名', '企业名称', '公司名称', 'name', 'title', 'label', '投资人']) {
            const name = item[key];
            if (typeof name === 'string' && name.trim()) return name.trim();
          }
          return '';
        }
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join('、') : undefined;
  }
  return undefined;
}

/**
 * 解析 JSON / markdown 围栏 / 标题前缀 + Python 单引号 dict。
 */
function tryParseIntelLooseObject(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const fenced = raw.match(/^```(?:json)?\s*([\s\S]*?)```$/i);
  const candidate = (fenced ? fenced[1] : raw).trim();

  const direct = tryParseIntelJson(candidate);
  if (direct) return direct;

  const braceStart = candidate.indexOf('{');
  const braceEnd = candidate.lastIndexOf('}');
  if (braceStart >= 0 && braceEnd > braceStart) {
    const inner = candidate.slice(braceStart, braceEnd + 1);
    const asJson = tryParseIntelJson(inner);
    if (asJson) return asJson;
    const asPy = tryParsePythonStyleDict(inner);
    if (asPy) return asPy;
  }
  return tryParsePythonStyleDict(candidate);
}

function tryParseIntelJson(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (!(raw.startsWith('{') || raw.startsWith('['))) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const firstObj = parsed.find((item) => item && typeof item === 'object' && !Array.isArray(item));
      return firstObj || null;
    }
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** 解析 {'公司名称': '宇树科技', '法定代表': '王兴兴'} 形式 */
function tryParsePythonStyleDict(text) {
  const src = String(text || '');
  if (!src.includes('{') || !src.includes(':')) return null;
  const map = {};
  let count = 0;
  const stringPair = /(['"])((?:\\.|(?!\1).)*)\1\s*:\s*(['"])((?:\\.|(?!\3).)*)\3/g;
  let match;
  while ((match = stringPair.exec(src)) !== null) {
    const key = unescapeIntelQuoted(match[2], match[1]);
    const value = unescapeIntelQuoted(match[4], match[3]);
    if (key && map[key] === undefined) {
      map[key] = value;
      count += 1;
    }
  }
  const scalarPair = /(['"])((?:\\.|(?!\1).)*)\1\s*:\s*(None|True|False|-?\d+(?:\.\d+)?)/g;
  while ((match = scalarPair.exec(src)) !== null) {
    const key = unescapeIntelQuoted(match[2], match[1]);
    if (!key || map[key] !== undefined) continue;
    const rawVal = match[3];
    if (rawVal === 'None') map[key] = '';
    else if (rawVal === 'True') map[key] = 'true';
    else if (rawVal === 'False') map[key] = 'false';
    else map[key] = rawVal;
    count += 1;
  }
  return count > 0 ? map : null;
}

function unescapeIntelQuoted(value, quote) {
  if (quote === "'") return value.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}

function parseIntelKeyValueText(text) {
  const map = {};
  for (const line of String(text || '').split(/\r?\n/)) {
    const matched = line.match(/^[\s*·\-•]*([^:：\t]{1,40})[:：\t]\s*(.+)$/);
    if (!matched) continue;
    const key = matched[1].trim();
    const value = matched[2].trim();
    const normalized = normalizeIntelValue(value);
    if (key && normalized !== undefined && map[key] === undefined) {
      map[key] = normalized;
    }
  }
  return map;
}

function pickIntelField(fieldIndex, keys) {
  for (const key of keys) {
    if (fieldIndex.map[key] !== undefined) return fieldIndex.map[key];
    const lower = String(key).toLowerCase();
    if (fieldIndex.lowerIndex[lower] !== undefined) return fieldIndex.lowerIndex[lower];
  }
  return undefined;
}

function consumeIntelField(fieldIndex, keys, usedKeys) {
  for (const key of keys) {
    if (fieldIndex.map[key] !== undefined) {
      usedKeys.add(key);
      usedKeys.add(String(key).toLowerCase());
      return fieldIndex.map[key];
    }
    const lower = String(key).toLowerCase();
    if (fieldIndex.lowerIndex[lower] !== undefined) {
      usedKeys.add(lower);
      for (const actual of Object.keys(fieldIndex.map)) {
        if (String(actual).toLowerCase() === lower) usedKeys.add(actual);
      }
      return fieldIndex.lowerIndex[lower];
    }
  }
  return undefined;
}

function formatIntelRawPayload(result) {
  const pack = {};
  if (result?.profile && typeof result.profile === 'object') {
    pack.profile = result.profile;
  }
  if (result?.structuredContent !== undefined) {
    pack.structuredContent = result.structuredContent;
  }
  if (result?.text) {
    pack.text = result.text;
  }
  if (Object.keys(pack).length === 0) return '';
  try {
    return JSON.stringify(pack, null, 2);
  } catch {
    return String(result?.text || '');
  }
}

/**
 * 将烯牛查询结果渲染为结构化区块；仅展示存在的字段。
 * 原始 JSON 仅放在折叠 details 中。
 */
function renderExternalIntelResult(body, result, fallbackCompanyName, aspect) {
  if (!body) return;
  const resolvedName = result.resolvedName || result.companyName || fallbackCompanyName;
  const queriedName = result.queriedName || fallbackCompanyName;
  const fieldIndex = parseIntelFieldMap(result);
  const usedKeys = new Set();

  const nameFromData = consumeIntelField(fieldIndex, [
    '公司工商名称', '企业名称', '公司名称', '公司全称', 'firm_name', 'companyName', 'name', 'format_name'
  ], usedKeys);
  const displayName = nameFromData || resolvedName;

  const facts = INTEL_FACT_SPECS
    .map((spec) => {
      // 公司名称已在标题展示，避免重复卡片
      if (spec.label === '公司工商名称') return null;
      const value = consumeIntelField(fieldIndex, spec.keys, usedKeys);
      return value === undefined ? null : { label: spec.label, value };
    })
    .filter(Boolean);

  const textBlocks = INTEL_TEXT_SPECS
    .map((spec) => {
      const value = consumeIntelField(fieldIndex, spec.keys, usedKeys);
      return value === undefined ? null : { label: spec.label, value };
    })
    .filter(Boolean);

  // 其余真实字段：以键值列表展示，永不 stringify 进主视图
  const extraFacts = [];
  for (const [key, value] of Object.entries(fieldIndex.map)) {
    if (INTEL_WRAP_KEYS.has(key)) continue;
    if (usedKeys.has(key) || usedKeys.has(String(key).toLowerCase())) continue;
    if (value === undefined || value === '') continue;
    // 过长文本走区块
    if (String(value).length > 80) {
      textBlocks.push({ label: key, value: String(value) });
    } else {
      extraFacts.push({ label: key, value: String(value) });
    }
    usedKeys.add(key);
  }

  const allFacts = facts.concat(extraFacts);
  const rawPayload = formatIntelRawPayload(result);
  const hasStructuredView = allFacts.length > 0 || textBlocks.length > 0;
  let plainFallback = '';
  if (!hasStructuredView) {
    // 仍无法解析时，给一句提示，原始内容只进 details
    plainFallback = '未能解析为结构化字段，请展开下方「查看原始返回」核对上游内容。';
  }

  const aliasHtml = result.resolvedFromAlias
    ? `<div class="intel-header-alias">由简称「${escapeHtml(queriedName)}」消歧为工商全称</div>`
    : '';

  const factsHtml = allFacts.length
    ? `<div>
        <div class="intel-section-title">关键工商信息</div>
        <div class="intel-facts">${allFacts.map((item) => `
          <div class="intel-fact">
            <div class="intel-fact-label">${escapeHtml(item.label)}</div>
            <div class="intel-fact-value">${escapeHtml(item.value)}</div>
          </div>`).join('')}</div>
      </div>`
    : '';

  const blocksHtml = textBlocks.map((item) => `
    <div>
      <div class="intel-section-title">${escapeHtml(item.label)}</div>
      <div class="intel-block-body">${escapeHtml(item.value)}</div>
    </div>`).join('');

  const plainHtml = plainFallback
    ? `<div>
        <div class="intel-section-title">提示</div>
        <div class="intel-block-body">${escapeHtml(plainFallback)}</div>
      </div>`
    : '';

  const rawHtml = rawPayload
    ? `<details class="intel-raw">
        <summary>查看原始返回（JSON / 文本）</summary>
        <pre>${escapeHtml(rawPayload)}</pre>
      </details>`
    : '';

  body.className = '';
  body.innerHTML = `
    <div class="intel-result">
      <div class="intel-header">
        <div class="intel-header-name">${escapeHtml(displayName)}</div>
        ${aliasHtml}
        <div class="intel-header-meta">来源：${escapeHtml(result.provider || 'xiniu')} · ${escapeHtml(result.aspect || aspect)}</div>
      </div>
      ${factsHtml}
      ${blocksHtml}
      ${plainHtml}
      <div class="intel-cite">${escapeHtml(providerCiteText(result.provider))}</div>
      ${rawHtml}
    </div>`;
}

/**
 * 渲染自动模式下的多源结果（每个源复用同一 profile 渲染器）。
 */
function renderExternalIntelMultiResult(body, payload, fallbackCompanyName, aspect) {
  if (!body) return;
  const results = Array.isArray(payload?.results) ? payload.results : [];
  if (results.length === 0) {
    setIntelResultMessage(body, '自动查询未返回有效结果。', 'error');
    return;
  }
  const answered = payload.answeredBy || results.map((item) => item.provider).join('+');
  const tabs = results.map((item, index) => {
    const name = providerDisplayName(item.provider);
    return `<button type="button" class="btn-secondary intel-provider-tab${index === 0 ? ' is-active' : ''}" data-intel-tab="${index}" style="padding:4px 10px;font-size:11px;">${escapeHtml(name)}</button>`;
  }).join('');
  const panels = results.map((item, index) => {
    const holderId = `intel-provider-panel-${index}`;
    return `<div id="${holderId}" class="intel-provider-panel" style="${index === 0 ? '' : 'display:none;'}"></div>`;
  }).join('');
  const errorHint = Array.isArray(payload.errors) && payload.errors.length
    ? `<div style="font-size:10px;color:#9CA3AF;margin-bottom:8px;">部分源失败：${escapeHtml(payload.errors.map((e) => `${providerDisplayName(e.provider)}=${e.code}`).join('；'))}</div>`
    : '';

  body.className = '';
  body.innerHTML = `
    <div class="intel-multi">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
        <div style="font-size:11px;color:#6B7280;">自动查询 · answeredBy=${escapeHtml(String(answered))}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">${tabs}</div>
      </div>
      ${errorHint}
      ${panels}
    </div>`;

  results.forEach((item, index) => {
    const panel = document.getElementById(`intel-provider-panel-${index}`);
    renderExternalIntelResult(panel, item, fallbackCompanyName, aspect);
  });

  body.querySelectorAll('.intel-provider-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.getAttribute('data-intel-tab'));
      body.querySelectorAll('.intel-provider-tab').forEach((el) => el.classList.remove('is-active'));
      btn.classList.add('is-active');
      body.querySelectorAll('.intel-provider-panel').forEach((panel, panelIdx) => {
        panel.style.display = panelIdx === idx ? '' : 'none';
      });
    });
  });
}

async function runKycScreenFromIntelDesk() {
  const companyName = String(document.getElementById('intel-company-input')?.value || '').trim();
  const body = document.getElementById('intel-result-body');
  const badge = document.getElementById('intel-result-badge');
  const stage = document.getElementById('stage-result-p15');
  if (!currentProjectId) {
    setIntelResultMessage(body, '请先在项目中选择或创建项目，再执行 KYC 初筛。', 'error');
    return;
  }
  if (!companyName) {
    setIntelResultMessage(body, '请输入企业名称后再执行 KYC 初筛。', 'error');
    return;
  }
  if (badge) {
    badge.textContent = 'KYC 筛查中…';
    badge.className = 'badge badge-gold';
  }
  setIntelResultMessage(body, `正在对「${companyName}」执行 KYC 初筛（UBO / 舆情 / 制裁）…`, 'empty');
  try {
    const output = await requestRuntime('/api/business/kyc/screen', {
      method: 'POST',
      body: JSON.stringify({ projectId: currentProjectId, companyName }),
      timeoutMs: 120000,
    });
    const snapshot = output.snapshot || {};
    const hits = Array.isArray(snapshot.hits) ? snapshot.hits : [];
    const riskLevel = snapshot.riskLevel || 'unknown';
    if (badge) {
      badge.textContent = `KYC · ${riskLevel}`;
      badge.className = riskLevel === 'high' ? 'badge badge-orange' : 'badge badge-green';
    }
    const lines = [
      `<div style="font-size:12px;font-weight:600;margin-bottom:8px;">风险等级：${escapeHtml(String(riskLevel))}</div>`,
      `<div style="font-size:11px;color:#374151;margin-bottom:8px;">${escapeHtml(String(snapshot.summary || ''))}</div>`,
      snapshot.uboSummary ? `<div style="font-size:11px;margin-bottom:8px;"><strong>UBO：</strong>${escapeHtml(String(snapshot.uboSummary))}</div>` : '',
      hits.length
        ? `<ul style="font-size:11px;padding-left:18px;margin:0;">${hits.map((hit) => `<li><strong>${escapeHtml(hit.title || hit.ruleId)}</strong>（${escapeHtml(hit.severity || '')}）— ${escapeHtml(hit.detail || '')}</li>`).join('')}</ul>`
        : '<div style="font-size:11px;color:#6B7280;">无规则命中</div>',
      `<p style="font-size:10px;color:#9CA3AF;margin-top:10px;">${escapeHtml(String(snapshot.disclaimer || ''))}</p>`,
    ].join('');
    if (body) body.innerHTML = lines;
    if (stage) {
      stage.textContent = `POST /api/business/kyc/screen · ${companyName} · risk=${riskLevel} · hits=${hits.length}`;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setIntelResultMessage(body, `KYC 初筛失败：${message}`, 'error');
    if (badge) {
      badge.textContent = 'KYC 失败';
      badge.className = 'badge badge-orange';
    }
  }
}

async function lookupExternalCompanyFromIntelDesk() {
  const companyName = String(document.getElementById('intel-company-input')?.value || '').trim();
  const aspect = String(document.getElementById('intel-aspect-select')?.value || '企业基本信息').trim();
  const provider = String(document.getElementById('intel-provider-select')?.value || 'auto').trim() || 'auto';
  const body = document.getElementById('intel-result-body');
  const badge = document.getElementById('intel-result-badge');
  const stage = document.getElementById('stage-result-p15');
  if (!companyName) {
    setIntelResultMessage(body, '请输入企业名称后再查询。', 'error');
    if (badge) {
      badge.textContent = '缺少企业名';
      badge.className = 'badge badge-orange';
    }
    return;
  }
  if (badge) {
    badge.textContent = '查询中…';
    badge.className = 'badge badge-gold';
  }
  setIntelResultMessage(body, `正在查询「${companyName}」· ${providerDisplayName(provider)} · ${aspect}…`, 'empty');
  try {
    const qs = new URLSearchParams({ companyName, aspect, provider });
    const result = await requestRuntime(`/api/business/external-data/company?${qs.toString()}`);
    if (result.provider === 'auto' && Array.isArray(result.results)) {
      const answeredLabel = result.answeredBy === 'both'
        ? '烯牛+企查查'
        : providerDisplayName(result.answeredBy || result.results[0]?.provider);
      if (badge) {
        badge.textContent = `${answeredLabel} · 有结果`;
        badge.className = 'badge badge-green';
      }
      renderExternalIntelMultiResult(body, result, companyName, aspect);
      const resolvedName = result.resolvedName || result.companyName || companyName;
      if (stage) {
        stage.textContent = `查询成功 · auto · ${resolvedName} · sources=${result.results.map((item) => item.provider).join(',')}`;
      }
      return;
    }
    if (badge) {
      badge.textContent = `${providerDisplayName(result.provider)} · 有结果`;
      badge.className = 'badge badge-green';
    }
    const resolvedName = result.resolvedName || result.companyName || companyName;
    renderExternalIntelResult(body, result, companyName, aspect);
    if (stage) {
      stage.textContent = `查询成功 · ${result.provider || provider} · ${resolvedName} · ${aspect}`;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    let label = '查询失败';
    if (/未配置|not_configured|XINIU_API_KEY|QCC_MCP|QCC_API_TOKEN|QICHACHA_/.test(message)) {
      label = '未配置';
    } else if (/upstream_empty|未找到企业|返回空结果/.test(message)) {
      label = '无匹配';
    } else if (/ambiguous_match|多个相似公司/.test(message)) {
      label = '需确认全称';
    } else if (/http_error|HTTP/.test(message)) {
      label = '上游 HTTP 错误';
    } else if (/parse_error|解析/.test(message)) {
      label = '解析失败';
    } else if (/timeout|超时/.test(message)) {
      label = '超时';
    }
    if (badge) {
      badge.textContent = label;
      badge.className = 'badge badge-orange';
    }
    setIntelResultMessage(
      body,
      label === '未配置'
        ? `外部数据未配置：请在本地 .env 设置对应密钥（XINIU_API_KEY 和/或 QCC_MCP_AUTHORIZATION / QCC_API_TOKEN）。\n不会编造企业信息。\n\n${message}`
        : `查询失败：${message}`,
      'error'
    );
    if (stage) stage.textContent = `查询失败：${message}`;
  }
}

document.getElementById('open-external-intel-btn')?.addEventListener('click', function() {
  document.querySelector('.nav-item[data-page="p15"]')?.click();
});
document.getElementById('intel-refresh-status-btn')?.addEventListener('click', function() {
  refreshXiniuStatus();
});
document.getElementById('datasources-refresh-btn')?.addEventListener('click', function() {
  void refreshDataProviders();
});
document.getElementById('intel-kyc-screen-btn')?.addEventListener('click', function() {
  void runKycScreenFromIntelDesk();
});
document.getElementById('intel-lookup-btn')?.addEventListener('click', function() {
  lookupExternalCompanyFromIntelDesk();
});
document.getElementById('intel-company-input')?.addEventListener('keydown', function(event) {
  if (event.key === 'Enter') {
    event.preventDefault();
    lookupExternalCompanyFromIntelDesk();
  }
});

// 启动时拉取一次烯牛状态（立项页徽章）
void refreshXiniuStatus();

void loadProjectList().then(async () => {
  const savedProjectId = localStorage.getItem('pe-ai-current-project');
  if (!savedProjectId) return;
  try {
    await openExistingProject(savedProjectId);
    await loadProjectList();
  } catch {
    localStorage.removeItem('pe-ai-current-project');
  }
});

void refreshRuntimeWhoami().then(() =>
  requestRuntime('/health', {}, false, true)
    .then(health => {
      const ai = health.aiCapability === 'llm'
        ? `LLM · ${health.llmModel || 'model'}`
        : '规则引擎';
      const roleHint = getRuntimeWhoami().role ? ` · ${getRuntimeWhoami().role}` : '';
      document.getElementById('runtime-status').textContent = `Runtime 已连接 · ${ai}${roleHint}`;
      const badge = document.getElementById('ai-mode-badge');
      if (badge) {
        badge.textContent = health.aiCapability === 'llm' ? 'LLM' : 'RULE';
        badge.title = health.aiCapability === 'llm'
          ? `大模型模式：${health.llmModel || ''}`
          : '本地规则引擎模式';
      }
    })
    .catch(error => {
      document.getElementById('runtime-status').textContent =
        String(error?.message || '').includes('鉴权失败')
          ? 'Runtime 鉴权失败 · 请输入 API 密钥'
          : '原型模式 · Runtime 离线';
    })
);

const portalUiDeps = {
  requestRuntime,
  showRuntimeError,
  setRuntimeProgress,
  getCurrentProjectId: () => currentProjectId,
  getCurrentProjectInput: () => currentProjectInput,
  getLatestContractReview: () => latestContractReview,
  getLatestDdQuestions: () => latestDdQuestions,
  runProjectSkill,
};

initValueCreationUi(portalUiDeps);
initPostInvestmentDashboardUi(portalUiDeps);
initRedTeamUi(portalUiDeps);
const postRiskApi = initPostRiskUi(portalUiDeps);
loadPostRiskClosures = postRiskApi.loadPostRiskClosures;
const knowledgeApi = initKnowledgeUi(portalUiDeps);
ensureKnowledgeBrowseLoaded = knowledgeApi.ensureLoaded;
const copilotApi = initCopilotUi(portalUiDeps);
ensureCopilotContextLoaded = copilotApi.ensureLoaded;
